import React from 'react';
import {Button, UncontrolledTooltip} from 'reactstrap';
import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";
import ObjectOverride from "components/ObjectOverride";
import Types from "constants/Types";
import Access from "constants/Access";
import MrpLocationControl from "components/Parts/MrpLocationControl";
import {applyMrpLocationToRequest, getMrpActiveLocation, getMrpLocationSignature, syncMrpActiveLocation} from "utils/mrpLocation";

const LOCATION_NOTE = 'ERP reads and writes follow the active location shown here. Changing it updates data across ERP screens.';


class MrpJob extends ObjectOverride {
	constructor(props) {
		super(props, Types.Mrp_Jobs._name);
		this.state = {
			loading: false,
			loadingData: true,
			jobName: '',
			modelId: '',
			lotId: '',
			jobQuantity: 1,
			serialRequiredStationId: '',
			stations: [],
			models: [],
			lots: [],
			dealers: [],
			manufacturers: [],
			calibers: [],
			productTypes: [],
			draftId: null,
			showLotModal: false,
			newLotNumber: '',
			lotError: '',
			validationErrors: {},
			modelLoadError: '',
			selectedModelDetails: null,
			selectedModelStations: [],
			jobModelStations: [],
			editModelRequirements: false,
			jobModelStationToAdd: '',
			jobModelPartSelections: {},
			...this.getDefaultModelModalState(),
		};
		this.modalOverlayMouseDownOnBackdrop = false;
	}

	componentDidMount() {
		syncMrpActiveLocation(this.props.rootState);
		this.loadInitialData().then();
	}

	componentDidUpdate(prevProps) {
		if (getMrpLocationSignature(prevProps?.rootState) !== getMrpLocationSignature(this.props?.rootState)) {
			this.setState({
				modelId: '',
				lotId: '',
				serialRequiredStationId: '',
				selectedModelDetails: null,
				selectedModelStations: [],
				jobModelStations: [],
				editModelRequirements: false,
				jobModelStationToAdd: '',
				jobModelPartSelections: {},
				modelLoadError: ''
			}, () => {
				this.loadInitialData().then();
			});
		}
	}

	getUserAccess = () => Number(this.props?.rootState?.userAccess ?? Access.Everyone)

	canCreateModels = () => this.getUserAccess() <= Access.Manager

	canManageMrpDefinitions = () => this.getUserAccess() <= Access.Manager

	getEmptyModelTransactionConfig = () => ({
		enabled: false,
		dealer: '',
		dealer_binary_id: '',
		dealer_name: '',
		bound_book_choice: '',
		bound_book: '',
		bound_book_binary_id: '',
		bound_book_name: '',
	})

	getEmptyModelAcquisitionConfig = () => this.getEmptyModelTransactionConfig()

	getEmptyModelTransferConfig = () => this.getEmptyModelTransactionConfig()

	getEmptyModelAssemblyOutputConfig = () => ({
		enabled: false,
		station_id: '',
		station_name: '',
		quantity_per_completed_unit: '1',
	})

	getDefaultModelModalState = () => ({
		showModelModal: false,
		editingModelId: '',
		newModelName: '',
		newModelType: 'firearm',
		newModelAssemblyOutput: this.getEmptyModelAssemblyOutputConfig(),
		newModelAcquisition: this.getEmptyModelAcquisitionConfig(),
		newModelTransfer: this.getEmptyModelTransferConfig(),
		modelError: '',
		modelStep: 1,
		newModelStationIds: [],
		newModelStationParts: {},
		newModelInlineItems: {},
		creatingModelPartStationId: '',
	})

	formatNamedLookupLabel = (entry) => `${entry?.name || entry?.label || entry?.title || ''}`.trim()

	getActiveMrpLocation = () => getMrpActiveLocation(this.props.rootState)?.selectedLocation || null

	getPrimaryBoundBookLabel = () => {
		const activeLocation = this.getActiveMrpLocation();
		return `${activeLocation?.bound_book || 'Primary'}`.trim() || 'Primary';
	}

	getActiveLocationBoundBookOptions = () => {
		const activeLocation = this.getActiveMrpLocation();
		if (!activeLocation) {
			return [];
		}
		const primaryLabel = this.getPrimaryBoundBookLabel();
		const additionalBoundBooks = Array.isArray(activeLocation.location_bound_bookss)
			? activeLocation.location_bound_bookss
			: [];
		return [
			{
				value: 'primary',
				label: `${primaryLabel} (Primary)`,
				bound_book: '',
				bound_book_binary_id: '',
				bound_book_name: primaryLabel
			},
			...additionalBoundBooks
				.map((boundBook) => {
					const pkey = `${boundBook?.pkey || ''}`.trim();
					const binaryId = `${boundBook?.binary_id || ''}`.trim();
					if (!pkey || !binaryId) {
						return null;
					}
					return {
						value: `${binaryId}-${pkey}`,
						label: `${boundBook?.name || `Bound Book ${pkey}`}`.trim(),
						bound_book: pkey,
						bound_book_binary_id: binaryId,
						bound_book_name: `${boundBook?.name || `Bound Book ${pkey}`}`.trim()
					};
				})
				.filter(Boolean)
		];
	}

	findBoundBookOption = (value) => {
		const normalizedValue = `${value || ''}`.trim();
		if (!normalizedValue) {
			return null;
		}
		return this.getActiveLocationBoundBookOptions().find((option) => option.value === normalizedValue) || null;
	}

	mapLookupItemsToOptions = (items) => (Array.isArray(items) ? items : [])
		.map((item) => {
			const id = `${item?.pkey || item?.id || ''}`.trim();
			const label = this.formatNamedLookupLabel(item);
			if (!id || !label) {
				return null;
			}
			return {id, label};
		})
		.filter(Boolean)

	normalizeAcquisitionLookupSelectedValue = (value) => {
		const normalizedValue = `${value || ''}`.trim();
		if (/^\d+$/.test(normalizedValue)) {
			return normalizedValue;
		}
		if (/^\d+-\d+$/.test(normalizedValue)) {
			return `${normalizedValue.split('-').pop() || ''}`.trim();
		}
		return '';
	}

	formatDealerLabel = (dealer) => {
		if (!dealer) {
			return 'Unknown Dealer';
		}
		const name = dealer.name_display || dealer.name_company || dealer.name_trade || dealer.name || `Dealer ${dealer.pkey || dealer.id}`;
		const ffl = dealer.ffl ? ` (${dealer.ffl})` : '';
		return `${name}${ffl}`;
	}

	isBackdropPointerEvent = (event) => Boolean(event && event.target === event.currentTarget)

	handleModalOverlayMouseDown = (event) => {
		this.modalOverlayMouseDownOnBackdrop = this.isBackdropPointerEvent(event);
	}

	handleModalOverlayClick = (closeHandler) => (event) => {
		const shouldClose = this.modalOverlayMouseDownOnBackdrop && this.isBackdropPointerEvent(event);
		this.modalOverlayMouseDownOnBackdrop = false;
		if (shouldClose && typeof closeHandler === 'function') {
			closeHandler();
		}
	}

	loadInitialData = async () => {
		this.setState({loadingData: true});
		try {
			await Promise.all([
				this.loadStations(),
				this.loadModels(),
				this.loadLots(),
				this.loadDealers(),
			]);
		} catch (error) {
		} finally {
			this.setState({loadingData: false});
		}
	}

	loadStations = async () => {
		let stationModel = new API.BuildClass(Types.Mrp_Stations._name, API.RequestType.READ_ALL);
		stationModel.parent = this.props.rootState.account_number;
		applyMrpLocationToRequest(stationModel, this.props.rootState);
		const result = await stationModel.submit(null, API.RequestType.READ_ALL);
		const stations = result.items.map(station => ({
			...station,
			id: station.pkey || station.id,
			parts: Array.isArray(station.parts) ? station.parts : [],
			selected: station.selected || false,
			laborMinutes: station.laborMinutes || 0,
			partMinutes: station.partMinutes || 0,
		}));
		this.setState({stations});
	}

	loadModels = async () => {
		let modelModel = new API.BuildClass(Types.Mrp_Models._name, API.RequestType.READ_ALL);
		modelModel.parent = this.props.rootState.account_number;
		applyMrpLocationToRequest(modelModel, this.props.rootState);
		const result = await modelModel.submit(null, API.RequestType.READ_ALL);
		const models = (result.items || [])
			.map((model) => this.normalizeModelRecord(model))
			.filter((model) => model.id && this.isMrpModelType(model.model_type));
		this.setState({models});
	}

	loadDealers = async () => {
		let dealerModel = new API.BuildClass(Types.Dealers._name, API.RequestType.READ_ALL);
		dealerModel.parent = this.props.rootState.account_number;
		dealerModel.limit = 1000;
		const result = await dealerModel.submit(null, API.RequestType.READ_ALL);
		const dealers = (result?.items || [])
			.map((dealer) => ({
				id: `${dealer.pkey || dealer.id || ''}`,
				binary_id: dealer.binary_id || '',
				label: this.formatDealerLabel(dealer)
			}))
			.filter((dealer) => dealer.id)
			.sort((a, b) => a.label.localeCompare(b.label));
		this.setState({dealers});
	}

	loadManufacturers = async () => {
		let manufacturerModel = new API.BuildClass(Types.Manufacturers._name, API.RequestType.READ_ALL);
		manufacturerModel.parent = this.props.rootState.account_number;
		manufacturerModel.limit = 1000;
		const result = await manufacturerModel.submit(null, API.RequestType.READ_ALL);
		const manufacturers = this.mapLookupItemsToOptions(result?.items);
		this.setState({manufacturers});
	}

	loadCalibers = async () => {
		let caliberModel = new API.BuildClass(Types.Calibers._name, API.RequestType.READ_ALL);
		caliberModel.parent = this.props.rootState.account_number;
		caliberModel.limit = 1000;
		const result = await caliberModel.submit(null, API.RequestType.READ_ALL);
		const calibers = this.mapLookupItemsToOptions(result?.items);
		this.setState({calibers});
	}

	loadProductTypes = async () => {
		let typeModel = new API.BuildClass(Types.Products_Types._name, API.RequestType.READ_ALL);
		typeModel.limit = 200;
		const result = await typeModel.submit(null, API.RequestType.READ_ALL);
		const productTypes = (Array.isArray(result?.items) ? result.items : [])
			.map((item) => {
				const value = this.formatNamedLookupLabel(item);
				if (!value) {
					return null;
				}
				return {value, label: value};
			})
			.filter(Boolean);
		this.setState({productTypes});
	}

	parseStationPartsList = (stationPartsListRaw) => {
		if (!stationPartsListRaw) {
			return {};
		}
		if (typeof stationPartsListRaw === 'object') {
			return stationPartsListRaw || {};
		}
		if (typeof stationPartsListRaw === 'string') {
			try {
				return JSON.parse(stationPartsListRaw) || {};
			} catch (error) {
				return {};
			}
		}
		return {};
	}

	parseAcquisitionLookupValue = (rawValue, rawOtherValue = '') => {
		const selectedValue = `${rawValue || ''}`.trim();
		const otherValue = `${rawOtherValue || ''}`.trim();
		const normalizedSelectedValue = this.normalizeAcquisitionLookupSelectedValue(selectedValue);
		return {
			selectedValue: normalizedSelectedValue,
			otherValue: normalizedSelectedValue ? otherValue : (otherValue || selectedValue)
		};
	}

	parseModelBoundBookConfig = (config = {}) => {
		const rawChoice = `${config.bound_book_choice || ''}`.trim();
		const boundBook = `${config.bound_book || ''}`.trim();
		const boundBookBinaryId = `${config.bound_book_binary_id || ''}`.trim();
		const boundBookName = `${config.bound_book_name || ''}`.trim();
		if (rawChoice === 'primary') {
			return {
				bound_book_choice: 'primary',
				bound_book: '',
				bound_book_binary_id: '',
				bound_book_name: boundBookName || this.getPrimaryBoundBookLabel()
			};
		}
		if (boundBook && boundBookBinaryId) {
			return {
				bound_book_choice: `${boundBookBinaryId}-${boundBook}`,
				bound_book: boundBook,
				bound_book_binary_id: boundBookBinaryId,
				bound_book_name: boundBookName
			};
		}
		if (this.findBoundBookOption(rawChoice)) {
			const option = this.findBoundBookOption(rawChoice);
			return {
				bound_book_choice: option.value,
				bound_book: option.bound_book,
				bound_book_binary_id: option.bound_book_binary_id,
				bound_book_name: option.bound_book_name
			};
		}
		return {
			bound_book_choice: '',
			bound_book: '',
			bound_book_binary_id: '',
			bound_book_name: ''
		};
	}

	parseModelTransactionConfig = (stationPartsListRaw, transactionType, modelName = '') => {
		const parsed = this.parseStationPartsList(stationPartsListRaw);
		const config = parsed?.[transactionType];
		if (!config || !config.enabled) {
			return this.getEmptyModelTransactionConfig();
		}
		const boundBook = this.parseModelBoundBookConfig(config);
		return {
			enabled: true,
			dealer: transactionType === 'transfer' ? `${config.dealer || ''}` : '',
			dealer_binary_id: transactionType === 'transfer' ? `${config.dealer_binary_id || ''}` : '',
			dealer_name: transactionType === 'transfer' ? `${config.dealer_name || ''}` : '',
			bound_book_choice: boundBook.bound_book_choice,
			bound_book: boundBook.bound_book,
			bound_book_binary_id: boundBook.bound_book_binary_id,
			bound_book_name: boundBook.bound_book_name,
			model: `${modelName || config.model || ''}`
		};
	}

	parseModelAcquisition = (stationPartsListRaw, modelName = '') => {
		return this.parseModelTransactionConfig(stationPartsListRaw, 'acquisition', modelName);
	}

	parseModelTransfer = (stationPartsListRaw, modelName = '') => {
		return this.parseModelTransactionConfig(stationPartsListRaw, 'transfer', modelName);
	}

	parseModelAssemblyOutput = (stationPartsListRaw) => {
		const parsed = this.parseStationPartsList(stationPartsListRaw);
		const config = parsed?.assembly_output;
		if (!config || !config.enabled) {
			return this.getEmptyModelAssemblyOutputConfig();
		}
		const quantityPerCompletedUnit = Number.parseFloat(config.quantity_per_completed_unit ?? config.output_quantity_per_unit ?? config.quantity_per_unit ?? 1);
		return {
			enabled: true,
			station_id: `${config.station_id || config.station || ''}`.trim(),
			station_name: `${config.station_name || ''}`.trim(),
			quantity_per_completed_unit: Number.isNaN(quantityPerCompletedUnit) || quantityPerCompletedUnit <= 0
				? '1'
				: `${quantityPerCompletedUnit}`,
		};
	}

	resolveModelType = (rawType, stationPartsListRaw) => {
		const normalizedRawType = `${rawType || ''}`.trim().toLowerCase();
		if (normalizedRawType === 'assembly' || normalizedRawType === 'firearm') {
			return normalizedRawType;
		}
		const parsed = this.parseStationPartsList(stationPartsListRaw);
		const parsedType = `${parsed.model_type || parsed.type || ''}`.trim().toLowerCase();
		if (parsedType === 'assembly' || parsedType === 'firearm') {
			return parsedType;
		}
		if (normalizedRawType === 'item' || parsedType === 'item') {
			return 'item';
		}
		return 'firearm';
	}

	isMrpModelType = (type) => ['firearm', 'assembly'].includes(`${type || ''}`.toLowerCase())

	isAssemblyModelType = (type) => `${type || ''}`.toLowerCase() === 'assembly'

	normalizeModelRecord = (model) => {
		const stationPartsList = model?.station_parts_list || model?.stationPartsList || '';
		const name = model?.name || '';
		const modelType = this.resolveModelType(model?.model_type || model?.type, stationPartsList);
		return {
			id: `${model?.pkey || model?.id || ''}`,
			name,
			description: model?.description || '',
			sku: model?.sku || '',
			station_parts_list: stationPartsList,
			model_type: modelType,
			assembly_output: this.parseModelAssemblyOutput(stationPartsList),
			acquisition: this.parseModelAcquisition(stationPartsList, name),
			transfer: this.parseModelTransfer(stationPartsList, name)
		};
	}

	buildModelModalState = (model) => {
		const normalizedModel = this.normalizeModelRecord(model);
		const configuredStations = this.parseModelStations(normalizedModel.station_parts_list);
		const newModelStationIds = configuredStations
			.map((station) => `${station.station_id || ''}`.trim())
			.filter(Boolean);
		const newModelStationParts = configuredStations.reduce((acc, station) => {
			const stationId = `${station.station_id || ''}`.trim();
			if (!stationId) {
				return acc;
			}
			acc[stationId] = (Array.isArray(station.parts) ? station.parts : []).reduce((partAcc, part) => {
				const partId = `${part.part_id || part.part_name || ''}`.trim();
				if (!partId) {
					return partAcc;
				}
				partAcc[partId] = `${part.quantity ?? ''}`;
				return partAcc;
			}, {});
			return acc;
		}, {});
		return {
			showModelModal: true,
			editingModelId: normalizedModel.id,
			newModelName: normalizedModel.name || '',
			newModelType: normalizedModel.model_type || 'firearm',
			newModelAssemblyOutput: normalizedModel.model_type === 'assembly'
				? normalizedModel.assembly_output
				: this.getEmptyModelAssemblyOutputConfig(),
			newModelAcquisition: normalizedModel.model_type === 'assembly'
				? this.getEmptyModelAcquisitionConfig()
				: normalizedModel.acquisition,
			newModelTransfer: normalizedModel.model_type === 'assembly'
				? this.getEmptyModelTransferConfig()
				: normalizedModel.transfer,
			modelError: '',
			modelStep: 1,
			newModelStationIds,
			newModelStationParts,
			newModelInlineItems: {},
			creatingModelPartStationId: '',
		};
	}

	loadLots = async () => {
		// TODO: API call - Load lots for account/location
		// Example:
		let lotModel = new API.BuildClass(Types.Mrp_Lots._name, API.RequestType.READ_ALL);
		lotModel.parent = this.props.rootState.account_number;
		applyMrpLocationToRequest(lotModel, this.props.rootState);
		const result = await lotModel.submit(null, API.RequestType.READ_ALL);
		const lotItems = result?.items ?? result;
		const lots = (Array.isArray(lotItems) ? lotItems : (lotItems ? [lotItems] : []))
			.map((lot) => ({
				id: lot.pkey || lot.id,
				lotNumber: lot.lotNumber || lot.number || lot.name || ''
			}))
			.filter((lot) => lot.id);
		this.setState({lots});
	}

	loadDraft = async (draftId) => {
		// TODO: API call - Load saved draft
		// Example:
		// let draftModel = new API.BuildClass(Types.Mrp_Job_Drafts._name, API.RequestType.READ);
		// draftModel.object.pkey = draftId;
		// const result = await draftModel.submit(null, API.RequestType.READ);
		// const draft = result.data;
		// this.setState({
		//   draftId: draft.pkey,
		//   jobName: draft.job_name || '',
		//   modelId: draft.model || '',
		//   lotId: draft.lot || '',
		//   jobQuantity: draft.quantity || 1,
		//   serialRequiredStationId: draft.serial_required_station || '',
		//   stations: this.mapDraftStations(draft.stations)
		// });
	}

	handleTextChange = (e) => {
		const {name, value} = e.target;
		this.setState({[name]: value, validationErrors: {...this.state.validationErrors, [name]: ''}});
	}

	handleNumberChange = (e) => {
		const {name, value} = e.target;
		const parsedValue = Math.max(0, parseInt(value || 0, 10));
		this.setState({
			[name]: Number.isNaN(parsedValue) ? 0 : parsedValue,
			validationErrors: {...this.state.validationErrors, [name]: ''}
		});
	}

	getModelTransactionStateKey = (transactionType) => {
		return transactionType === 'transfer' ? 'newModelTransfer' : 'newModelAcquisition';
	}

	handleNewModelTransactionToggle = (transactionType, event) => {
		const enabled = !!event?.target?.checked;
		const stateKey = this.getModelTransactionStateKey(transactionType);
		const oppositeStateKey = this.getModelTransactionStateKey(transactionType === 'transfer' ? 'acquisition' : 'transfer');
		this.setState((prevState) => ({
			[stateKey]: {
				...(prevState[stateKey] || this.getEmptyModelTransactionConfig()),
				enabled
			},
			[oppositeStateKey]: enabled ? {
				...(prevState[oppositeStateKey] || this.getEmptyModelTransactionConfig()),
				enabled: false
			} : prevState[oppositeStateKey],
			modelError: ''
		}));
	}

	handleNewModelAcquisitionToggle = (event) => {
		this.handleNewModelTransactionToggle('acquisition', event);
	}

	handleNewModelTransferToggle = (event) => {
		this.handleNewModelTransactionToggle('transfer', event);
	}

	handleNewModelTransactionFieldChange = (transactionType, event) => {
		const {name, value} = event.target;
		const stateKey = this.getModelTransactionStateKey(transactionType);
		this.setState((prevState) => ({
			[stateKey]: {
				...(prevState[stateKey] || this.getEmptyModelTransactionConfig()),
				[name]: value
			},
			modelError: ''
		}));
	}

	handleNewModelAcquisitionFieldChange = (event) => {
		this.handleNewModelTransactionFieldChange('acquisition', event);
	}

	handleNewModelTransferFieldChange = (event) => {
		this.handleNewModelTransactionFieldChange('transfer', event);
	}

	handleNewModelTransactionLookupChange = (transactionType, field) => (event) => {
		const value = this.normalizeAcquisitionLookupSelectedValue(event?.target?.value);
		const stateKey = this.getModelTransactionStateKey(transactionType);
		this.setState((prevState) => ({
			[stateKey]: {
				...(prevState[stateKey] || this.getEmptyModelTransactionConfig()),
				[field]: value
			},
			modelError: ''
		}));
	}

	handleNewModelAcquisitionLookupChange = (field) => this.handleNewModelTransactionLookupChange('acquisition', field)

	handleNewModelTransferLookupChange = (field) => this.handleNewModelTransactionLookupChange('transfer', field)

	handleNewModelDealerChange = (transactionType, event) => {
		const dealerId = `${event.target.value || ''}`;
		const selectedDealer = this.state.dealers.find((dealer) => dealer.id === dealerId) || null;
		const stateKey = this.getModelTransactionStateKey(transactionType);
		this.setState((prevState) => ({
			[stateKey]: {
				...(prevState[stateKey] || this.getEmptyModelTransactionConfig()),
				dealer: dealerId,
				dealer_binary_id: selectedDealer?.binary_id || '',
				dealer_name: selectedDealer?.label || ''
			},
			modelError: ''
		}));
	}

	handleNewModelBoundBookChange = (transactionType, event) => {
		const selectedOption = this.findBoundBookOption(event?.target?.value);
		const stateKey = this.getModelTransactionStateKey(transactionType);
		this.setState((prevState) => ({
			[stateKey]: {
				...(prevState[stateKey] || this.getEmptyModelTransactionConfig()),
				bound_book_choice: selectedOption?.value || '',
				bound_book: selectedOption?.bound_book || '',
				bound_book_binary_id: selectedOption?.bound_book_binary_id || '',
				bound_book_name: selectedOption?.bound_book_name || ''
			},
			modelError: ''
		}));
	}

	handleNewModelAcquisitionDealerChange = (event) => {
		this.handleNewModelDealerChange('acquisition', event);
	}

	handleNewModelTransferDealerChange = (event) => {
		this.handleNewModelDealerChange('transfer', event);
	}

	handleNewModelAssemblyOutputToggle = (event) => {
		const enabled = !!event?.target?.checked;
		this.setState((prevState) => ({
			newModelAssemblyOutput: enabled
				? {
					...(prevState.newModelAssemblyOutput || this.getEmptyModelAssemblyOutputConfig()),
					enabled: true
				}
				: this.getEmptyModelAssemblyOutputConfig(),
			modelError: ''
		}));
	}

	handleNewModelAssemblyOutputStationChange = (event) => {
		const stationId = `${event.target.value || ''}`.trim();
		const selectedStation = this.state.stations.find((station) => `${station.id}` === stationId) || null;
		this.setState((prevState) => ({
			newModelAssemblyOutput: {
				...(prevState.newModelAssemblyOutput || this.getEmptyModelAssemblyOutputConfig()),
				enabled: true,
				station_id: stationId,
				station_name: selectedStation?.name || ''
			},
			modelError: ''
		}));
	}

	handleNewModelAssemblyOutputQuantityChange = (event) => {
		const value = `${event.target.value || ''}`;
		this.setState((prevState) => ({
			newModelAssemblyOutput: {
				...(prevState.newModelAssemblyOutput || this.getEmptyModelAssemblyOutputConfig()),
				enabled: true,
				quantity_per_completed_unit: value
			},
			modelError: ''
		}));
	}

	isAcquisitionEnabledForModel = (modelDetails = null) => {
		return !!(modelDetails?.acquisition?.enabled);
	}

	isTransferEnabledForModel = (modelDetails = null) => {
		return !!(modelDetails?.transfer?.enabled);
	}

	getEnabledAutoTransactionType = (modelDetails = null) => {
		if (this.isAssemblyModelType(modelDetails?.model_type)) {
			return '';
		}
		const acquisitionEnabled = this.isAcquisitionEnabledForModel(modelDetails);
		const transferEnabled = this.isTransferEnabledForModel(modelDetails);
		if (acquisitionEnabled && transferEnabled) {
			return 'multiple';
		}
		if (transferEnabled) {
			return 'transfer';
		}
		if (acquisitionEnabled) {
			return 'acquisition';
		}
		return '';
	}

	handleModelChange = (e) => {
		const value = `${e.target.value || ''}`;
		this.setState({
			modelId: value,
			validationErrors: {...this.state.validationErrors, modelId: '', serialRequiredStationId: ''},
			modelLoadError: '',
			editModelRequirements: false,
			jobModelStationToAdd: '',
			jobModelPartSelections: {},
		});
		this.applyModelSelection(value);
	}

	applyModelSelection = (modelId, modelsOverride = null) => {
		const selectedModelId = `${modelId || ''}`;
		if (!selectedModelId) {
			this.setState((prevState) => ({
				selectedModelDetails: null,
				selectedModelStations: [],
				jobModelStations: [],
				serialRequiredStationId: '',
				stations: prevState.stations.map((station) => ({
					...station,
					selected: false
				}))
			}));
			return;
		}
		const models = Array.isArray(modelsOverride) ? modelsOverride : this.state.models;
		const selectedModel = models.find((model) => `${model.id}` === selectedModelId);
		if (!selectedModel) {
			this.setState({
				modelLoadError: 'Model details not found. Please refresh.'
			});
			return;
		}
		const stationPartsListRaw = selectedModel.station_parts_list;
		const modelType = this.resolveModelType(selectedModel.model_type, stationPartsListRaw);
		const selectedModelStations = this.parseModelStations(stationPartsListRaw);
		const jobModelStations = this.cloneModelStations(selectedModelStations);
		const selectedModelDetails = {
			id: `${selectedModel.id}`,
			name: selectedModel.name || '',
			description: selectedModel.description || '',
			sku: selectedModel.sku || '',
			station_parts_list: stationPartsListRaw,
			model_type: modelType,
			assembly_output: selectedModel.assembly_output || this.getEmptyModelAssemblyOutputConfig(),
			acquisition: modelType === 'assembly'
				? this.getEmptyModelAcquisitionConfig()
				: (selectedModel.acquisition || this.getEmptyModelAcquisitionConfig()),
			transfer: modelType === 'assembly'
				? this.getEmptyModelTransferConfig()
				: (selectedModel.transfer || this.getEmptyModelTransferConfig()),
		};
		this.setState({
			modelId: selectedModelId,
			selectedModelDetails,
			selectedModelStations,
			jobModelStations,
			modelLoadError: ''
		}, () => {
			this.applyModelToStations(jobModelStations);
		});
	}

	cloneModelStations = (stations) => {
		return (stations || []).map((station) => ({
			station_id: station.station_id ? `${station.station_id}` : '',
			station_name: station.station_name,
			order: station.order,
			parts: (station.parts || []).map((part) => ({
				part_id: part.part_id ? `${part.part_id}` : '',
				part_name: part.part_name,
				quantity: part.quantity
			}))
		}));
	}

	parseModelStations = (stationPartsListRaw) => {
		if (!stationPartsListRaw) {
			return [];
		}
		let parsed = stationPartsListRaw;
		if (typeof stationPartsListRaw === 'string') {
			try {
				parsed = JSON.parse(stationPartsListRaw);
			} catch (error) {
				return [];
			}
		}
		const stations = Array.isArray(parsed?.stations) ? parsed.stations : [];
		return stations.map((station, index) => {
			const stationId = station.station_id || station.stationId || station.id;
			const normalizedStationId = stationId ? `${stationId}` : '';
			const stationName = station.station_name || station.stationName || station.name || '';
			const orderValue = Number.parseInt(station.order ?? index + 1, 10);
			const order = Number.isNaN(orderValue) ? index + 1 : orderValue;
			const parts = Array.isArray(station.parts) ? station.parts : [];
			const normalizedParts = parts.map((part) => {
				const partId = part.part_id || part.partId || part.id || part.name;
				const partName = part.part_name || part.partName || part.name || '';
				const normalizedPartId = partId || partName;
				const quantityValue = part.quantity ?? part.qty ?? part.required_qty ?? part.required_quantity ?? '';
				const quantity = Number.parseFloat(quantityValue);
				return {
					part_id: normalizedPartId ? `${normalizedPartId}` : '',
					part_name: partName,
					quantity: Number.isNaN(quantity) ? 0 : quantity
				};
			}).filter((part) => part.part_id || part.part_name);

			if (!stationId && !stationName) {
				return null;
			}

			return {
				station_id: normalizedStationId,
				station_name: stationName,
				order,
				parts: normalizedParts
			};
		}).filter(Boolean).sort((a, b) => (a.order || 0) - (b.order || 0));
	}

	applyModelToStations = (modelStations) => {
		const modelStationIds = (modelStations || [])
			.map((station) => station.station_id)
			.filter(Boolean)
			.map((id) => `${id}`);
		const modelStationIdSet = new Set(modelStationIds);
		this.setState((prevState) => {
			const stationMap = new Map(prevState.stations.map((station) => [`${station.id}`, station]));
			const orderedSelected = modelStationIds
				.map((stationId) => stationMap.get(`${stationId}`))
				.filter(Boolean)
				.map((station) => ({...station, selected: true}));
			const remaining = prevState.stations
				.filter((station) => !modelStationIdSet.has(`${station.id}`))
				.map((station) => ({...station, selected: false}));
			const serialRequiredStationId = modelStationIdSet.has(`${prevState.serialRequiredStationId}`)
				? prevState.serialRequiredStationId
				: '';
			return {
				stations: [...orderedSelected, ...remaining],
				serialRequiredStationId,
				validationErrors: {...prevState.validationErrors, stations: ''}
			};
		});
	}

	normalizeJobModelStations = (stations) => {
		return (stations || []).map((station, index) => ({
			...station,
			order: index + 1
		}));
	}

	handleToggleEditModelRequirements = () => {
		this.setState((prevState) => ({
			editModelRequirements: !prevState.editModelRequirements
		}));
	}

	resetJobModelToDefaults = () => {
		const jobModelStations = this.cloneModelStations(this.state.selectedModelStations);
		this.setState({
			jobModelStations,
			jobModelStationToAdd: '',
			jobModelPartSelections: {},
		}, () => {
			this.applyModelToStations(jobModelStations);
		});
	}

	handleJobModelStationAdd = () => {
		const stationIdValue = this.state.jobModelStationToAdd;
		if (!stationIdValue) return;
		const stationId = `${stationIdValue}`;
		const station = this.state.stations.find((entry) => `${entry.id}` === stationId);
		if (!station) return;
		this.setState((prevState) => {
			if (prevState.jobModelStations.some((entry) => `${entry.station_id}` === stationId)) {
				return {jobModelStationToAdd: ''};
			}
			const nextStations = [
				...prevState.jobModelStations,
				{
					station_id: stationId,
					station_name: station.name || 'Station',
					order: prevState.jobModelStations.length + 1,
					parts: []
				}
			];
			return {
				jobModelStations: this.normalizeJobModelStations(nextStations),
				jobModelStationToAdd: ''
			};
		}, () => {
			this.applyModelToStations(this.state.jobModelStations);
		});
	}

	handleJobModelStationRemove = (stationId) => {
		const stationKey = `${stationId}`;
		this.setState((prevState) => {
			const nextStations = prevState.jobModelStations.filter((station) => `${station.station_id}` !== stationKey);
			const jobModelPartSelections = {...prevState.jobModelPartSelections};
			delete jobModelPartSelections[stationKey];
			return {
				jobModelStations: this.normalizeJobModelStations(nextStations),
				jobModelPartSelections
			};
		}, () => {
			this.applyModelToStations(this.state.jobModelStations);
		});
	}

	handleJobModelStationMove = (stationId, direction) => () => {
		const stationKey = `${stationId}`;
		this.setState((prevState) => {
			const index = prevState.jobModelStations.findIndex((station) => `${station.station_id}` === stationKey);
			const swapIndex = direction === 'up' ? index - 1 : index + 1;
			if (index < 0 || swapIndex < 0 || swapIndex >= prevState.jobModelStations.length) {
				return null;
			}
			const nextStations = [...prevState.jobModelStations];
			[nextStations[index], nextStations[swapIndex]] = [nextStations[swapIndex], nextStations[index]];
			return {jobModelStations: this.normalizeJobModelStations(nextStations)};
		}, () => {
			this.applyModelToStations(this.state.jobModelStations);
		});
	}

	handleJobModelPartSelectionChange = (stationId) => (e) => {
		const stationKey = `${stationId}`;
		const value = e.target.value;
		this.setState((prevState) => ({
			jobModelPartSelections: {
				...prevState.jobModelPartSelections,
				[stationKey]: value
			}
		}));
	}

	handleJobModelPartAdd = (stationId) => {
		const stationKey = `${stationId}`;
		const partIdValue = this.state.jobModelPartSelections[stationKey];
		if (!partIdValue) return;
		const partId = `${partIdValue}`;
		const station = this.state.stations.find((entry) => `${entry.id}` === stationKey);
		const partSource = station?.parts?.find((part) => `${part.pkey || part.id || part.name}` === partId);
		if (!partSource) return;
		this.setState((prevState) => {
			const nextStations = prevState.jobModelStations.map((stationItem) => {
				if (`${stationItem.station_id}` !== stationKey) return stationItem;
				if (stationItem.parts.some((part) => `${part.part_id}` === partId)) return stationItem;
				return {
					...stationItem,
					parts: [
						...stationItem.parts,
						{
							part_id: partId,
							part_name: partSource.name || partSource.part_name || partId,
							quantity: partSource.quantity || 1
						}
					]
				};
			});
			return {
				jobModelStations: nextStations,
				jobModelPartSelections: {
					...prevState.jobModelPartSelections,
					[stationKey]: ''
				}
			};
		});
	}

	handleJobModelPartRemove = (stationId, partIdValue) => {
		const stationKey = `${stationId}`;
		const partId = `${partIdValue}`;
		this.setState((prevState) => ({
			jobModelStations: prevState.jobModelStations.map((stationItem) => {
				if (`${stationItem.station_id}` !== stationKey) return stationItem;
				return {
					...stationItem,
					parts: stationItem.parts.filter((part) => `${part.part_id}` !== partId)
				};
			})
		}));
	}

	handleJobModelPartQuantityChange = (stationId, partIdValue) => (e) => {
		const stationKey = `${stationId}`;
		const partId = `${partIdValue}`;
		const value = e.target.value;
		this.setState((prevState) => ({
			jobModelStations: prevState.jobModelStations.map((stationItem) => {
				if (`${stationItem.station_id}` !== stationKey) return stationItem;
				return {
					...stationItem,
					parts: stationItem.parts.map((part) => {
						if (`${part.part_id}` !== partId) return part;
						return {
							...part,
							quantity: value
						};
					})
				};
			})
		}));
	}

	getModelPartsSummary = (modelStations) => {
		const summary = {};
		modelStations.forEach((station) => {
			const stationName = station.station_name || this.getStationNameById(station.station_id);
			(station.parts || []).forEach((part) => {
				const partId = part.part_id || part.partId || part.id || part.part_name || part.name;
				const partName = part.part_name || part.partName || part.name || partId || 'Unknown Item';
				const summaryKey = partId || partName;
				if (!summaryKey) {
					return;
				}
				const quantityValue = Number.parseFloat(part.quantity);
				if (Number.isNaN(quantityValue) || quantityValue <= 0) {
					return;
				}
				if (!summary[summaryKey]) {
					summary[summaryKey] = {
						part_id: partId || summaryKey,
						part_name: partName,
						total_quantity: 0,
						quantity_on_hand: this.getStationPartQuantityOnHand(station.station_id, part),
						stations: []
					};
				}
				summary[summaryKey].total_quantity += quantityValue;
				summary[summaryKey].stations.push({
					station_name: stationName,
					quantity: quantityValue
				});
			});
		});
		return Object.values(summary).sort((a, b) => a.part_name.localeCompare(b.part_name));
	}

	getStationPartQuantityOnHand = (stationId, part) => {
		const stationKey = `${stationId || ''}`;
		const partId = `${part?.part_id || part?.partId || part?.id || ''}`.trim();
		const partName = `${part?.part_name || part?.partName || part?.name || ''}`.trim().toLowerCase();
		const station = this.state.stations.find((entry) => `${entry.id}` === stationKey);
		const stationParts = Array.isArray(station?.parts) ? station.parts : [];
		const matchedById = partId
			? stationParts.find((stationPart) => `${stationPart?.pkey || stationPart?.id || ''}`.trim() === partId)
			: null;
		const matchedByName = partName
			? stationParts.find((stationPart) => `${stationPart?.name || stationPart?.part_name || ''}`.trim().toLowerCase() === partName)
			: null;
		const matchedPart = matchedById || matchedByName || null;
		const quantityOnHand = Number.parseFloat(matchedPart?.quantity);
		return Number.isNaN(quantityOnHand) ? 0 : quantityOnHand;
	}

	getModelCapacitySummary = (modelStations) => {
		const items = this.getModelPartsSummary(modelStations).map((part) => {
			const quantityOnHand = Number.parseFloat(part.quantity_on_hand);
			const normalizedQuantityOnHand = Number.isNaN(quantityOnHand) ? 0 : quantityOnHand;
			const totalRequiredQuantity = Number.parseFloat(part.total_quantity);
			const normalizedTotalRequiredQuantity = Number.isNaN(totalRequiredQuantity) ? 0 : totalRequiredQuantity;
			const maxUnits = normalizedTotalRequiredQuantity > 0
				? Math.max(0, Math.floor(normalizedQuantityOnHand / normalizedTotalRequiredQuantity))
				: null;
			return {
				...part,
				quantity_on_hand: normalizedQuantityOnHand,
				max_units: maxUnits,
			};
		});
		const capacityItems = items.filter((part) => part.max_units !== null);
		const maxUnits = capacityItems.length > 0
			? capacityItems.reduce((min, part) => Math.min(min, part.max_units), Number.POSITIVE_INFINITY)
			: null;
		const limitingParts = capacityItems.filter((part) => part.max_units === maxUnits);
		return {
			items,
			maxUnits: Number.isFinite(maxUnits) ? maxUnits : null,
			limitingParts,
		};
	}

	getStationNameById = (stationId) => {
		if (!stationId) {
			return 'Unknown Station';
		}
		const station = this.state.stations.find((entry) => `${entry.id}` === `${stationId}`);
		return station ? station.name : 'Unknown Station';
	}

	normalizeQuantityValue = (value) => {
		const parsedValue = Number.parseFloat(value);
		return Number.isNaN(parsedValue) ? 0 : parsedValue;
	}

	normalizeAssemblyOutputQuantityPerCompletedUnit = (value) => {
		const parsedValue = Number.parseFloat(value);
		if (Number.isNaN(parsedValue) || parsedValue <= 0) {
			return 1;
		}
		return parsedValue;
	}

	normalizeModelStationsForCompare = (stations) => {
		return (stations || []).map((station, index) => ({
			station_id: station.station_id ? `${station.station_id}` : '',
			order: station.order ?? index + 1,
			parts: (station.parts || []).map((part) => ({
				part_id: part.part_id ? `${part.part_id}` : '',
				quantity: this.normalizeQuantityValue(part.quantity)
			})).filter((part) => part.part_id).sort((a, b) => a.part_id.localeCompare(b.part_id))
		})).filter((station) => station.station_id).sort((a, b) => {
			if (a.order === b.order) {
				return a.station_id.localeCompare(b.station_id);
			}
			return a.order - b.order;
		});
	}

	hasModelOverrides = () => {
		const defaultStations = this.normalizeModelStationsForCompare(this.state.selectedModelStations);
		const jobStations = this.normalizeModelStationsForCompare(this.state.jobModelStations);
		return JSON.stringify(defaultStations) !== JSON.stringify(jobStations);
	}

	formatQuantity = (value) => {
		const parsedValue = Number.parseFloat(value);
		if (Number.isNaN(parsedValue)) {
			return '-';
		}
		return Number.isInteger(parsedValue) ? parsedValue : parsedValue.toFixed(2);
	}

	buildWorkflowPayload = () => {
		const trimmedJobName = this.state.jobName.trim();
		const modelDetails = this.state.selectedModelDetails || {};
		const modelName = modelDetails.name || '';
		const modelType = this.resolveModelType(modelDetails.model_type, null);
		const acquisitionConfig = this.isAcquisitionEnabledForModel(modelDetails)
			? {
				...modelDetails.acquisition,
				model: modelName
			}
			: this.getEmptyModelAcquisitionConfig();
		const transferConfig = this.isTransferEnabledForModel(modelDetails)
			? {
				...modelDetails.transfer,
				model: modelName
			}
			: this.getEmptyModelTransferConfig();
		const assemblyOutputConfig = this.isAssemblyModelType(modelType) && modelDetails?.assembly_output?.enabled
			? {
				...modelDetails.assembly_output,
				station_id: `${modelDetails?.assembly_output?.station_id || ''}`.trim(),
				station_name: `${modelDetails?.assembly_output?.station_name || ''}`.trim(),
				quantity_per_completed_unit: this.normalizeAssemblyOutputQuantityPerCompletedUnit(
					modelDetails?.assembly_output?.quantity_per_completed_unit
				)
			}
			: this.getEmptyModelAssemblyOutputConfig();
		const jobDisplayName = trimmedJobName || (modelName ? `${modelName} Job` : 'ERP Job');
		const selectedStations = this.getSelectedStations();
		const stationMeta = selectedStations.reduce((acc, station, index) => {
			const stationId = `${station.id}`;
			acc[stationId] = {
				station_id: stationId,
				station_name: station.name || this.getStationNameById(stationId),
				order: index + 1,
				labor_minutes: station.laborMinutes || 0,
				part_minutes: station.partMinutes || 0,
			};
			return acc;
		}, {});
		const stations = (this.state.jobModelStations || []).map((station, index) => {
			const stationId = station.station_id ? `${station.station_id}` : '';
			const meta = stationMeta[stationId] || {};
			const parts = (station.parts || []).map((part) => {
				const requiredQuantity = this.normalizeQuantityValue(part.quantity);
				return {
					part_id: part.part_id ? `${part.part_id}` : '',
					part_name: part.part_name || '',
					required_quantity: requiredQuantity,
					completed_quantity: 0,
					time_entries: [],
				};
			}).filter((part) => part.part_id || part.part_name);
			const totalRequiredQuantity = parts.reduce((sum, part) => sum + (part.required_quantity || 0), 0);
			return {
				station_id: stationId,
				station_name: station.station_name || meta.station_name || this.getStationNameById(stationId),
				order: station.order ?? meta.order ?? index + 1,
				labor_minutes: meta.labor_minutes ?? 0,
				part_minutes: meta.part_minutes ?? 0,
				status: 'pending',
				started_at: null,
				completed_at: null,
				parts,
				totals: {
					required_parts: parts.length,
					required_quantity: totalRequiredQuantity,
				},
			};
		}).filter((station) => station.station_id);
		return {
			version: 1,
			created_at: new Date().toISOString(),
			job: {
				name: jobDisplayName,
				quantity: this.state.jobQuantity,
				lot_id: this.state.lotId,
				model_id: this.state.modelId,
				model_type: modelType,
				serial_required_station_id: this.state.serialRequiredStationId || '',
				acquisition_enabled: acquisitionConfig.enabled,
				transfer_enabled: transferConfig.enabled,
				status: 'in_progress',
			},
			model: {
				id: this.state.modelId,
				name: modelName,
				description: modelDetails.description || '',
				sku: modelDetails.sku || '',
				model_type: modelType,
				assembly_output: assemblyOutputConfig,
				acquisition: acquisitionConfig,
				transfer: transferConfig,
			},
			selected_stations: selectedStations.map((station, index) => ({
				station_id: `${station.id}`,
				station_name: station.name || this.getStationNameById(station.id),
				order: index + 1,
				labor_minutes: station.laborMinutes || 0,
				part_minutes: station.partMinutes || 0,
			})),
			stations,
			acquisition: {
				enabled: acquisitionConfig.enabled,
				transaction_collection: null,
				completed_units: [],
			},
			transfer: {
				enabled: transferConfig.enabled,
				transaction_collection: null,
				completed_units: [],
			},
			serial_tracking: {
				required_station_id: this.state.serialRequiredStationId || '',
				pending_completion: null,
			},
			model_stations: this.cloneModelStations(this.state.selectedModelStations),
			has_overrides: this.hasModelOverrides(),
		};
	}

	handleLotChange = (e) => {
		const value = e.target.value;
		this.setState({
			lotId: value,
			validationErrors: {...this.state.validationErrors, lotId: ''}
		});
	}

	handleShowModelModal = () => {
		if (!this.canCreateModels()) {
			API.Toast.error('Only owners/managers can create models.');
			return;
		}
		this.setState({
			...this.getDefaultModelModalState(),
			showModelModal: true,
		});
	}

	handleEditModelModal = (model = null) => {
		if (!this.canCreateModels()) {
			API.Toast.error('Only owners/managers can edit models.');
			return;
		}
		const targetModel = model || this.state.models.find((entry) => `${entry.id}` === `${this.state.modelId}`);
		if (!targetModel?.id) {
			API.Toast.error('Select a model to edit.');
			return;
		}
		this.setState(this.buildModelModalState(targetModel));
	}

	handleDeleteModel = async (model = null) => {
		if (!this.canCreateModels()) {
			API.Toast.error('Only owners/managers can delete models.');
			return;
		}
		const targetModel = model || this.state.models.find((entry) => `${entry.id}` === `${this.state.modelId}`);
		if (!targetModel?.id) {
			API.Toast.error('Select a model to delete.');
			return;
		}
		if (!window.confirm(`Are you sure you want to delete "${targetModel.name || 'this model'}"? This action cannot be undone.`)) {
			return;
		}

		this.setState({loading: true});
		try {
			const modelId = `${targetModel.id}`;
			let modelRequest = new API.BuildClass(Types.Mrp_Models._name, API.RequestType.DELETE);
			modelRequest.object.pkey = modelId;
			applyMrpLocationToRequest(modelRequest, this.props.rootState);
			modelRequest.parent = modelId;
			await modelRequest.submit(null, API.RequestType.DELETE);

			const remainingModels = this.state.models.filter((entry) => `${entry.id}` !== modelId);
			const deletedSelectedModel = `${this.state.modelId}` === modelId;
			this.setState((prevState) => ({
				models: remainingModels,
				...(deletedSelectedModel ? {
					modelId: '',
					selectedModelDetails: null,
					selectedModelStations: [],
					jobModelStations: [],
					serialRequiredStationId: '',
					editModelRequirements: false,
					jobModelStationToAdd: '',
					jobModelPartSelections: {},
					modelLoadError: '',
					validationErrors: {...prevState.validationErrors, modelId: ''},
					stations: prevState.stations.map((station) => ({
						...station,
						selected: false
					}))
				} : {})
			}));
			API.Toast.success('Model deleted successfully');
		} catch (error) {
			API.Toast.error('Failed to delete model');
			console.error('Error deleting model:', error);
		} finally {
			this.setState({loading: false});
		}
	}

	handleCloseModelModal = () => {
		this.modalOverlayMouseDownOnBackdrop = false;
		this.setState(this.getDefaultModelModalState());
	}

	getSelectedModelStations = () => {
		return this.state.newModelStationIds
			.map((stationId) => this.state.stations.find((station) => station.id === stationId))
			.filter(Boolean);
	}

	getSelectedModelParts = (station) => {
		const parts = Array.isArray(station.parts) ? station.parts : [];
		const selections = this.state.newModelStationParts[station.id] || {};
		return parts.reduce((acc, part) => {
			const partKey = part.pkey || part.id || part.name;
			const rawValue = selections[partKey];
			if (rawValue === undefined || rawValue === null || rawValue === '') {
				return acc;
			}
			const quantity = parseFloat(rawValue);
			if (Number.isNaN(quantity) || quantity <= 0) {
				return acc;
			}
			acc.push({
				part_id: part.pkey || part.id || part.name,
				part_name: part.name,
				quantity
			});
			return acc;
		}, []);
	}

	handleAddModelStation = (stationId) => {
		this.setState((prevState) => {
			if (prevState.newModelStationIds.includes(stationId)) {
				return null;
			}
			return {
				newModelStationIds: [...prevState.newModelStationIds, stationId],
				modelError: ''
			};
		});
	}

	handleRemoveModelStation = (stationId) => {
		this.setState((prevState) => {
			const newModelStationIds = prevState.newModelStationIds.filter((id) => id !== stationId);
			const newModelStationParts = {...prevState.newModelStationParts};
			const newModelInlineItems = {...prevState.newModelInlineItems};
			delete newModelStationParts[stationId];
			delete newModelInlineItems[stationId];
			return {
				newModelStationIds,
				newModelStationParts,
				newModelInlineItems,
				modelError: ''
			};
		});
	}

	moveModelStation = (stationId, direction) => () => {
		this.setState((prevState) => {
			const index = prevState.newModelStationIds.indexOf(stationId);
			const swapIndex = direction === 'up' ? index - 1 : index + 1;
			if (index < 0 || swapIndex < 0 || swapIndex >= prevState.newModelStationIds.length) {
				return null;
			}
			const newModelStationIds = [...prevState.newModelStationIds];
			[newModelStationIds[index], newModelStationIds[swapIndex]] = [newModelStationIds[swapIndex], newModelStationIds[index]];
			return {newModelStationIds};
		});
	}

	handleModelPartQuantityChange = (stationId, partId) => (e) => {
		const value = e.target.value;
		this.setState((prevState) => ({
			newModelStationParts: {
				...prevState.newModelStationParts,
				[stationId]: {
					...(prevState.newModelStationParts[stationId] || {}),
					[partId]: value
				}
			},
			modelError: ''
		}));
	}

	getEmptyModelItemDraft = () => ({
		item: '',
		description: '',
		qoh: '',
		requiredQty: '1',
	})

	getModelItemDraft = (stationId) => {
		return {
			...this.getEmptyModelItemDraft(),
			...(this.state.newModelInlineItems[stationId] || {})
		};
	}

	handleModelItemDraftChange = (stationId, field) => (e) => {
		const value = e.target.value;
		this.setState((prevState) => ({
			newModelInlineItems: {
				...prevState.newModelInlineItems,
				[stationId]: {
					...this.getEmptyModelItemDraft(),
					...(prevState.newModelInlineItems[stationId] || {}),
					[field]: value
				}
			},
			modelError: ''
		}));
	}

	handleCreateModelStationItem = async (station) => {
		if (!station) {
			return;
		}
		const stationId = station.id;
		if (!stationId && stationId !== 0) {
			return;
		}
		const stationName = station.name || 'this station';
		const draft = this.getModelItemDraft(stationId);
		const itemName = `${draft.item || ''}`.trim();
		const itemDescription = `${draft.description || ''}`.trim();
		const qohRaw = `${draft.qoh ?? ''}`.trim();
		const requiredQtyRaw = `${draft.requiredQty ?? ''}`.trim();

		if (!itemName) {
			this.setState({modelError: `Enter an item name for ${stationName}.`});
			return;
		}

		if (qohRaw === '') {
			this.setState({modelError: `Enter QoH for ${stationName}.`});
			return;
		}

		const qohValue = Number.parseFloat(qohRaw);
		if (Number.isNaN(qohValue) || qohValue < 0) {
			this.setState({modelError: `QoH must be a valid number for ${stationName}.`});
			return;
		}

		const requiredQtyValue = Number.parseFloat(requiredQtyRaw);
		if (Number.isNaN(requiredQtyValue) || requiredQtyValue <= 0) {
			this.setState({modelError: `Required qty must be greater than 0 for ${stationName}.`});
			return;
		}

		this.setState({
			creatingModelPartStationId: `${stationId}`,
			modelError: ''
		});

		try {
			let partModel = new API.BuildClass(Types.Mrp_Parts._name, API.RequestType.CREATE);
			partModel.object.name = itemName;
			partModel.object.description = itemDescription;
			partModel.object.quantity = `${qohValue}`;
			partModel.object.station = stationId;
			applyMrpLocationToRequest(partModel, this.props.rootState);
			partModel.parent = this.props.rootState.account_number;
			const result = await partModel.submit(null, API.RequestType.CREATE, true);
			const createdPartId = `${result?.pkey || result?.id || ''}`;
			if (!createdPartId) {
				throw new Error('Missing part id from create response');
			}

			const createdPart = {
				...result,
				pkey: result?.pkey || createdPartId,
				id: result?.id || createdPartId,
				name: result?.name || itemName,
				description: result?.description ?? itemDescription,
				quantity: result?.quantity ?? `${qohValue}`,
				station: result?.station ?? stationId,
			};

			this.setState((prevState) => ({
				stations: prevState.stations.map((entry) => {
					if (`${entry.id}` !== `${stationId}`) {
						return entry;
					}
					const existingParts = Array.isArray(entry.parts) ? entry.parts : [];
					const existingIndex = existingParts.findIndex((part) => `${part.pkey || part.id || part.name}` === createdPartId);
					if (existingIndex >= 0) {
						const nextParts = [...existingParts];
						nextParts[existingIndex] = createdPart;
						return {
							...entry,
							parts: nextParts
						};
					}
					return {
						...entry,
						parts: [...existingParts, createdPart]
					};
				}),
				newModelStationParts: {
					...prevState.newModelStationParts,
					[stationId]: {
						...(prevState.newModelStationParts[stationId] || {}),
						[createdPartId]: `${requiredQtyValue}`
					}
				},
				newModelInlineItems: {
					...prevState.newModelInlineItems,
					[stationId]: this.getEmptyModelItemDraft()
				}
			}));

			API.Toast.success(`Item added to ${stationName}`);
		} catch (error) {
			API.Toast.error('Failed to add item');
			this.setState({
				modelError: `Could not add a new item for ${stationName}.`
			});
			console.error('Error creating station item in model flow:', error);
		} finally {
			this.setState({creatingModelPartStationId: ''});
		}
	}

	initializeModelPartDefaults = () => {
		// No-op: items start un-added; user explicitly adds via "+" button
	}

	validateNewModelTransactionSelection = () => {
		if (this.state.newModelAcquisition.enabled && this.state.newModelTransfer.enabled) {
			return 'Enable either acquisition auto-create or transfer auto-create, not both.';
		}
		return '';
	}

	validateNewModelTransactionConfig = (transactionType) => {
		const config = this.state[this.getModelTransactionStateKey(transactionType)] || this.getEmptyModelTransactionConfig();
		if (!config.enabled) {
			return '';
		}
		const selectedBoundBook = this.findBoundBookOption(config.bound_book_choice);
		if (!selectedBoundBook) {
			return transactionType === 'transfer'
				? 'Choose which bound book should receive this ERP transfer.'
				: 'Choose which bound book should receive acquisition-created products.';
		}
		if (transactionType === 'transfer' && !`${config.dealer || ''}`.trim()) {
			return transactionType === 'transfer'
				? 'Transfer to dealer is required when transfer is enabled.'
				: 'Acquired from dealer is required when acquisition is enabled.';
		}
		return '';
	}

	validateNewModelAcquisitionConfig = () => this.validateNewModelTransactionConfig('acquisition')

	validateNewModelTransferConfig = () => this.validateNewModelTransactionConfig('transfer')

	validateNewModelAssemblyOutputConfig = () => {
		if (this.state.newModelType !== 'assembly') {
			return '';
		}
		const config = this.state.newModelAssemblyOutput || this.getEmptyModelAssemblyOutputConfig();
		if (!config.enabled) {
			return '';
		}
		if (!`${config.station_id || ''}`.trim()) {
			return 'Choose the station that should receive this completed assembly.';
		}
		const quantityPerCompletedUnit = Number.parseFloat(config.quantity_per_completed_unit);
		if (Number.isNaN(quantityPerCompletedUnit) || quantityPerCompletedUnit <= 0) {
			return 'Enter how many station items should be added for each completed assembly unit.';
		}
		return '';
	}

	serializeModelTransactionConfig = (transactionType, config, modelName) => {
		if (!config?.enabled) {
			return {enabled: false};
		}
		const serializedConfig = {
			enabled: true,
			model: modelName
		};
		if (transactionType === 'transfer') {
			serializedConfig.dealer = `${config.dealer || ''}`.trim();
			serializedConfig.dealer_binary_id = `${config.dealer_binary_id || ''}`.trim();
			serializedConfig.dealer_name = `${config.dealer_name || ''}`.trim();
		}
		const selectedBoundBook = this.findBoundBookOption(config.bound_book_choice);
		if (selectedBoundBook) {
			serializedConfig.bound_book_choice = selectedBoundBook.value || '';
			serializedConfig.bound_book = selectedBoundBook.bound_book || '';
			serializedConfig.bound_book_binary_id = selectedBoundBook.bound_book_binary_id || '';
			serializedConfig.bound_book_name = selectedBoundBook.bound_book_name || '';
		}
		return serializedConfig;
	}

	handleModelNextStep = () => {
		if (!this.state.newModelName.trim()) {
			this.setState({modelError: 'Please enter a model name'});
			return;
		}
		if (this.state.newModelStationIds.length === 0) {
			this.setState({modelError: 'Select at least one station for this model'});
			return;
		}
		const transactionSelectionError = this.validateNewModelTransactionSelection();
		if (transactionSelectionError) {
			this.setState({modelError: transactionSelectionError});
			return;
		}
		const acquisitionValidationError = this.validateNewModelAcquisitionConfig();
		if (acquisitionValidationError) {
			this.setState({modelError: acquisitionValidationError});
			return;
		}
		const transferValidationError = this.validateNewModelTransferConfig();
		if (transferValidationError) {
			this.setState({modelError: transferValidationError});
			return;
		}
		const assemblyOutputValidationError = this.validateNewModelAssemblyOutputConfig();
		if (assemblyOutputValidationError) {
			this.setState({modelError: assemblyOutputValidationError});
			return;
		}
		this.initializeModelPartDefaults();
		this.setState({modelStep: 2, modelError: ''});
	}

	handleModelPrevStep = () => {
		this.setState({modelStep: 1, modelError: ''});
	}

	handleSaveModel = async () => {
		const isEditingModel = !!this.state.editingModelId;
		if (!this.canCreateModels()) {
			API.Toast.error(`Only owners/managers can ${isEditingModel ? 'edit' : 'create'} models.`);
			return;
		}
		if (this.state.loading) {
			return;
		}
		const editingModelId = `${this.state.editingModelId || ''}`.trim();
		const modelName = this.state.newModelName.trim();
		if (!modelName) {
			this.setState({modelError: 'Please enter a model name'});
			return;
		}

		const selectedStations = this.getSelectedModelStations();
		if (selectedStations.length === 0) {
			this.setState({modelError: 'Select at least one station for this model'});
			return;
		}
		const transactionSelectionError = this.validateNewModelTransactionSelection();
		if (transactionSelectionError) {
			this.setState({modelError: transactionSelectionError});
			return;
		}
		const acquisitionValidationError = this.validateNewModelAcquisitionConfig();
		if (acquisitionValidationError) {
			this.setState({modelError: acquisitionValidationError});
			return;
		}
		const transferValidationError = this.validateNewModelTransferConfig();
		if (transferValidationError) {
			this.setState({modelError: transferValidationError});
			return;
		}
		const assemblyOutputValidationError = this.validateNewModelAssemblyOutputConfig();
		if (assemblyOutputValidationError) {
			this.setState({modelError: assemblyOutputValidationError});
			return;
		}

		const stationPayload = selectedStations.map((station, index) => ({
			station_id: station.id,
			station_name: station.name,
			order: index + 1,
			parts: this.getSelectedModelParts(station)
		}));
		const savedAcquisitionConfig = this.state.newModelType === 'assembly'
			? this.getEmptyModelAcquisitionConfig()
			: this.serializeModelTransactionConfig('acquisition', this.state.newModelAcquisition, modelName);
		const savedTransferConfig = this.state.newModelType === 'assembly'
			? this.getEmptyModelTransferConfig()
			: this.serializeModelTransactionConfig('transfer', this.state.newModelTransfer, modelName);
		// const stationsMissingParts = stationPayload
		// 	.filter((station) => station.parts.length === 0)
		// 	.map((station) => station.station_name);
		// if (stationsMissingParts.length > 0) {
		// 	this.setState({
		// 		modelError: `Add at least one part for: ${stationsMissingParts.join(', ')}`
		// 	});
		// 	return;
		// }

		const stationPartsList = {
			version: 1,
			model_type: this.state.newModelType,
			station_count: stationPayload.length,
			stations: stationPayload,
			assembly_output: this.state.newModelType === 'assembly'
				? {
					enabled: !!this.state.newModelAssemblyOutput?.enabled,
					station_id: `${this.state.newModelAssemblyOutput?.station_id || ''}`.trim(),
					station_name: `${this.state.newModelAssemblyOutput?.station_name || ''}`.trim(),
					quantity_per_completed_unit: this.normalizeAssemblyOutputQuantityPerCompletedUnit(
						this.state.newModelAssemblyOutput?.quantity_per_completed_unit
					),
				}
				: this.getEmptyModelAssemblyOutputConfig(),
			acquisition: savedAcquisitionConfig,
			transfer: savedTransferConfig
		};

		this.setState({loading: true, modelError: ''});
		try {
			const requestType = isEditingModel ? API.RequestType.UPDATE : API.RequestType.CREATE;
			let modelModel = new API.BuildClass(Types.Mrp_Models._name, requestType);
			if (isEditingModel) {
				modelModel.object.pkey = editingModelId;
			}
			modelModel.object.name = modelName;
			modelModel.object.station_parts_list = JSON.stringify(stationPartsList);
			applyMrpLocationToRequest(modelModel, this.props.rootState);
			modelModel.parent = isEditingModel ? editingModelId : this.props.rootState.account_number;
			const result = await modelModel.submit(null, requestType, isEditingModel);
			const savedModel = this.normalizeModelRecord({
				...this.state.models.find((model) => `${model.id}` === editingModelId),
				...result,
				pkey: result?.pkey || editingModelId,
				id: result?.id || editingModelId,
				name: result?.name || modelName,
				station_parts_list: result?.station_parts_list || JSON.stringify(stationPartsList),
				model_type: this.state.newModelType
			});
			const nextModels = isEditingModel
				? this.state.models.map((model) => (`${model.id}` === savedModel.id ? savedModel : model))
				: [...this.state.models, savedModel];
			const shouldSelectSavedModel = !isEditingModel || `${this.state.modelId}` === editingModelId;
			this.setState((prevState) => ({
				models: nextModels,
				modelId: shouldSelectSavedModel ? savedModel.id : prevState.modelId,
				modelLoadError: '',
				editModelRequirements: false,
				jobModelStationToAdd: '',
				jobModelPartSelections: {},
				validationErrors: {...prevState.validationErrors, modelId: ''},
				...this.getDefaultModelModalState(),
			}), () => {
				if (shouldSelectSavedModel) {
					this.applyModelSelection(savedModel.id, nextModels);
				}
			});
			API.Toast.success(isEditingModel ? 'Model updated successfully' : 'Model created successfully');
		} catch (error) {
			API.Toast.error(`Failed to ${isEditingModel ? 'update' : 'create'} model`);
			console.error(`Error ${isEditingModel ? 'updating' : 'creating'} model:`, error);
		} finally {
			this.setState({loading: false});
		}
	}

	handleShowLotModal = () => {
		if (!this.canManageMrpDefinitions()) {
			API.Toast.error('Only owners/managers can create lots.');
			return;
		}
		this.setState({showLotModal: true, newLotNumber: '', lotError: ''});
	}

	handleCloseLotModal = () => {
		this.modalOverlayMouseDownOnBackdrop = false;
		this.setState({showLotModal: false, newLotNumber: '', lotError: ''});
	}

	handleSaveLot = async () => {
		if (!this.canManageMrpDefinitions()) {
			API.Toast.error('Only owners/managers can create lots.');
			return;
		}
		if (this.state.loading) {
			return;
		}
		if (!this.state.newLotNumber.trim()) {
			this.setState({lotError: 'Please enter a lot number'});
			return;
		}
		// TODO: API call - Create new lot

		this.setState({loading: true, lotError: ''});
		try {
			let lotModel = new API.BuildClass(Types.Mrp_Lots._name, API.RequestType.CREATE);
			lotModel.parent = this.props.rootState.account_number;
			lotModel.object.name = this.state.newLotNumber.trim();
			applyMrpLocationToRequest(lotModel, this.props.rootState);
			const result = await lotModel.submit(null, API.RequestType.CREATE);
			const newLot = {id: result.pkey, lotNumber: this.state.newLotNumber.trim()};
				this.setState({
					lots: [...this.state.lots, newLot],
					lotId: result.pkey,
					showLotModal: false,
					newLotNumber: ''
				});
			API.Toast.success('Lot created successfully');
		} catch (error) {
			API.Toast.error('Failed to create lot');
			console.error('Error creating lot:', error);
		} finally {
			this.setState({loading: false});
		}
	}

	validateForm = () => {
		const errors = {};
		const autoTransactionType = this.getEnabledAutoTransactionType(this.state.selectedModelDetails);
		const requiresAutoTransactionSerialGate = ['acquisition', 'transfer', 'multiple'].includes(autoTransactionType);
		if (!this.state.modelId) {
			errors.modelId = 'Model is required';
		}
		if (autoTransactionType === 'multiple') {
			errors.modelId = 'Model cannot enable both acquisition and transfer auto-create.';
		}
		if (!this.state.lotId) {
			errors.lotId = 'Lot is required';
		}
		if (!this.state.jobQuantity || this.state.jobQuantity < 1) {
			errors.jobQuantity = 'Quantity must be at least 1';
		}
		const selectedStations = this.state.stations.filter(s => s.selected);
		if (selectedStations.length === 0) {
			errors.stations = 'At least one station must be selected';
		}
		if (requiresAutoTransactionSerialGate && !this.state.serialRequiredStationId) {
			errors.serialRequiredStationId = autoTransactionType === 'transfer'
				? 'Serial required station is required for transfer-enabled models'
				: 'Serial required station is required for auto-create-enabled models';
		}
		this.setState({validationErrors: errors});
		return Object.keys(errors).length === 0;
	}


	createJob = async () => {
		if (!this.validateForm()) {
			API.Toast.error('Please fix validation errors before creating job');
			return;
		}

		this.setState({loading: true});
		try {
			const workflowPayload = this.buildWorkflowPayload();
			let jobModel = new API.BuildClass(Types.Mrp_Jobs._name, API.RequestType.CREATE);
			jobModel.object.name = workflowPayload.job.name;
			jobModel.object.model = this.state.modelId;
			jobModel.object.lot = this.state.lotId;
			jobModel.object.status = workflowPayload.job.status;
			jobModel.object.time_started = workflowPayload.created_at;
			jobModel.object.workflow = JSON.stringify(workflowPayload);
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			jobModel.parent = this.props.rootState.account_number;
			await jobModel.submit(null, API.RequestType.CREATE);
			API.Toast.success('Job created successfully');
			this.props.history.push('/erp');
		} catch (error) {
			API.Toast.error('Failed to create job');
			console.error('Error creating job:', error);
		} finally {
			this.setState({loading: false});
		}
	}

	handleStationToggle = (stationId) => () => {
		this.setState((prevState) => {
			const stations = prevState.stations.map((station) => {
				if (station.id !== stationId) return station;
				return {...station, selected: !station.selected};
			});
			const toggledStation = stations.find((station) => station.id === stationId);
			let jobModelStations = prevState.jobModelStations;
			if (prevState.modelId && toggledStation) {
				const stationKey = `${stationId}`;
				const existingIndex = jobModelStations.findIndex((station) => `${station.station_id}` === stationKey);
				if (toggledStation.selected && existingIndex === -1) {
					jobModelStations = [
						...jobModelStations,
						{
							station_id: stationKey,
							station_name: toggledStation.name || 'Station',
							order: jobModelStations.length + 1,
							parts: []
						}
					];
				}
				if (!toggledStation.selected && existingIndex !== -1) {
					jobModelStations = jobModelStations.filter((station) => `${station.station_id}` !== stationKey);
				}
				jobModelStations = this.normalizeJobModelStations(jobModelStations);
			}
			const selectedIds = stations.filter((station) => station.selected).map((station) => station.id);
			const serialRequiredStationId = selectedIds.includes(prevState.serialRequiredStationId)
				? prevState.serialRequiredStationId
				: '';
			return {
				stations,
				serialRequiredStationId,
				jobModelStations
			};
		}, () => {
			if (this.state.modelId) {
				this.applyModelToStations(this.state.jobModelStations);
			}
		});
	}

	moveStation = (stationId, direction) => () => {
		this.setState((prevState) => {
			const index = prevState.stations.findIndex((station) => station.id === stationId);
			const swapIndex = direction === 'up' ? index - 1 : index + 1;
			if (index < 0 || swapIndex < 0 || swapIndex >= prevState.stations.length) {
				return null;
			}
			const stations = [...prevState.stations];
			[stations[index], stations[swapIndex]] = [stations[swapIndex], stations[index]];
			let jobModelStations = prevState.jobModelStations;
			if (prevState.modelId) {
				const orderedSelectedIds = stations
					.filter((station) => station.selected)
					.map((station) => `${station.id}`);
				const orderedStations = orderedSelectedIds
					.map((stationIdValue) => jobModelStations.find((station) => `${station.station_id}` === stationIdValue))
					.filter(Boolean);
				const remainingStations = jobModelStations.filter((station) => !orderedSelectedIds.includes(`${station.station_id}`));
				jobModelStations = this.normalizeJobModelStations([...orderedStations, ...remainingStations]);
			}
			return {stations, jobModelStations};
		}, () => {
			if (this.state.modelId) {
				this.applyModelToStations(this.state.jobModelStations);
			}
		});
	}

	getSelectedStations() {
		return this.state.stations.filter((station) => station.selected);
	}

	getTransactionTypeOptions = (config) => {
		if (
			config?.type
			&& !this.state.productTypes.some((option) => option.value === config.type)
		) {
			return [{value: config.type, label: config.type}, ...this.state.productTypes];
		}
		return this.state.productTypes;
	}

	renderModelTransactionSection = ({
		transactionType,
		title,
		helpText,
		toggleLabel,
		dealerLabel,
		locationNote = ''
	}) => {
		const stateKey = this.getModelTransactionStateKey(transactionType);
		const config = this.state[stateKey] || this.getEmptyModelTransactionConfig();
		const oppositeTransactionType = transactionType === 'transfer' ? 'acquisition' : 'transfer';
		const oppositeStateKey = this.getModelTransactionStateKey(oppositeTransactionType);
		const oppositeConfig = this.state[oppositeStateKey] || this.getEmptyModelTransactionConfig();
		const isBlockedByOppositeMode = !!oppositeConfig.enabled && !config.enabled;
		const fieldPrefix = transactionType === 'transfer' ? 'newModelTransfer' : 'newModelAcquisition';
		const activeLocation = this.getActiveMrpLocation();
		const boundBookOptions = this.getActiveLocationBoundBookOptions();

		return (
			<div style={{...modalStyles.section, marginTop: '18px'}}>
				<div className="d-flex align-items-center justify-content-between flex-wrap">
					<div>
						<div style={modalStyles.sectionTitle}>{title}</div>
						<div style={modalStyles.inlineHelp}>{helpText}</div>
					</div>
					<label className="mb-0 d-flex align-items-center" style={{gap: '8px', fontWeight: 600}}>
						<input
							type="checkbox"
							checked={!!config.enabled}
							disabled={isBlockedByOppositeMode}
							onChange={(event) => this.handleNewModelTransactionToggle(transactionType, event)}
						/>
						{toggleLabel}
					</label>
				</div>
				{isBlockedByOppositeMode && (
					<div className="small text-muted mt-2">
						Turn off {oppositeTransactionType} auto-create before enabling {transactionType} auto-create.
					</div>
				)}
				{config.enabled && (
					<div className="mt-3">
						<div className="row">
							<div className="col-md-6">
								<div style={modalStyles.inputGroup}>
									<label htmlFor={`${fieldPrefix}BoundBook`} style={modalStyles.label}>
										Bound Book
									</label>
									<select
										id={`${fieldPrefix}BoundBook`}
										value={config.bound_book_choice}
										onChange={(event) => this.handleNewModelBoundBookChange(transactionType, event)}
										style={modalStyles.inputField}
									>
										<option value="">
											Select a bound book
										</option>
										{boundBookOptions.map((boundBook) => (
											<option key={boundBook.value} value={boundBook.value}>
												{boundBook.label}
											</option>
										))}
									</select>
									<div className="small text-muted mt-2">
										Uses the bound books available on {activeLocation?.name_display || activeLocation?.name || 'the active ERP location'}.
									</div>
								</div>
							</div>
						</div>
						<div className="row">
							{transactionType === 'transfer' ? (
								<div className="col-md-6">
									<div style={modalStyles.inputGroup}>
										<label htmlFor={`${fieldPrefix}Dealer`} style={modalStyles.label}>
											{dealerLabel}
										</label>
										<select
											id={`${fieldPrefix}Dealer`}
											value={config.dealer}
											onChange={(event) => this.handleNewModelDealerChange(transactionType, event)}
											style={modalStyles.inputField}
										>
											<option value="">Select a dealer</option>
											{this.state.dealers.map((dealer) => (
												<option key={dealer.id} value={dealer.id}>
													{dealer.label}
												</option>
											))}
										</select>
									</div>
								</div>
							) : (
								<div className="col-md-6">
									<div style={modalStyles.inputGroup}>
										<label style={modalStyles.label}>
											Acquired From
										</label>
										<div className="small text-muted" style={{paddingTop: '10px'}}>
											Always recorded from self/internal at the active ERP location.
										</div>
									</div>
								</div>
							)}
						</div>
						<div className="small text-muted mt-2">
							Manufacturer, caliber, type, and UPC are captured in ERP Work when the operator enters the serial at the serial-gated station.
						</div>
						{locationNote && (
							<div className="small text-muted mt-2">{locationNote}</div>
						)}
					</div>
				)}
			</div>
		);
	}

	render() {
		if (this.state.loadingData) {
			return <Framework.Elements.LoadingSpinner isLoading={true}/>;
		}
		const isEditingModelModal = !!this.state.editingModelId;
		const selectedStations = this.getSelectedStations();
		const newModelStations = this.getSelectedModelStations();
		const availableModelStations = this.state.stations.filter(
			(station) => !this.state.newModelStationIds.includes(station.id)
		);
		const modelStations = this.state.jobModelStations || [];
		const modelCapacitySummary = this.getModelCapacitySummary(modelStations);
		const modelPartsSummary = modelCapacitySummary.items;
		const modelPartsTotal = modelPartsSummary.reduce((sum, part) => sum + (part.total_quantity || 0), 0);
		const maxAvailableUnits = modelCapacitySummary.maxUnits;
		const hasModelInventoryCapacity = maxAvailableUnits !== null;
		const exceedsAvailableUnits = hasModelInventoryCapacity && this.state.jobQuantity > maxAvailableUnits;
		const limitingPartNames = modelCapacitySummary.limitingParts
			.map((part) => part.part_name || part.part_id || 'Unknown Item')
			.filter(Boolean);
		const missingModelStations = modelStations.filter(
			(station) => station.station_id && !this.state.stations.some((entry) => `${entry.id}` === `${station.station_id}`)
		);
		const availableJobModelStations = this.state.stations.filter(
			(station) => !modelStations.some((modelStation) => `${modelStation.station_id}` === `${station.id}`)
		);
		const isEditingModelRequirements = this.state.editModelRequirements;
		const hasModelOverrides = this.hasModelOverrides();
		const canCreateModels = this.canCreateModels();
		const canManageMrpDefinitions = this.canManageMrpDefinitions();
		const totalStationCount = this.state.stations.length;
		const selectedStationCount = selectedStations.length;
		const stationCoveragePercent = totalStationCount > 0 ? Math.round((selectedStationCount / totalStationCount) * 100) : 0;
		const selectedLot = this.state.lots.find((lot) => `${lot.id}` === `${this.state.lotId}`);
		const modelTypeLabel = this.isAssemblyModelType(this.state.selectedModelDetails?.model_type) ? 'Assembly' : 'Firearm';
		const selectedModelAcquisition = this.state.selectedModelDetails?.acquisition || this.getEmptyModelAcquisitionConfig();
		const selectedModelTransfer = this.state.selectedModelDetails?.transfer || this.getEmptyModelTransferConfig();
		const selectedModelAutoTransactionType = this.getEnabledAutoTransactionType(this.state.selectedModelDetails);
		const selectedModelAutoTransactionConfig = selectedModelAutoTransactionType === 'transfer'
			? selectedModelTransfer
			: selectedModelAcquisition;
		const selectedModelAutoTransactionLabel = selectedModelAutoTransactionType === 'transfer'
			? 'Transfer'
			: selectedModelAutoTransactionType === 'acquisition'
				? 'Acquisition'
				: selectedModelAutoTransactionType === 'multiple'
					? 'Conflict'
					: 'Off';
		const requiresAutoTransactionSerialGate = ['acquisition', 'transfer', 'multiple'].includes(selectedModelAutoTransactionType);
		const readinessChecks = [
			{label: 'Model selected', done: Boolean(this.state.modelId)},
			{label: 'Lot selected', done: Boolean(this.state.lotId)},
			{label: 'Station workflow selected', done: selectedStationCount > 0},
			{label: 'Quantity set', done: this.state.jobQuantity >= 1},
			{label: 'Serial gate set', done: !requiresAutoTransactionSerialGate || Boolean(this.state.serialRequiredStationId)},
		];
		const readinessCompletedCount = readinessChecks.filter((check) => check.done).length;
		const readinessPercent = Math.round((readinessCompletedCount / readinessChecks.length) * 100);
		const orderedSelectedStations = this.state.stations.filter((station) => station.selected);
		const firstStationName = orderedSelectedStations[0]?.name || 'Not selected';
		const finalStationName = orderedSelectedStations[orderedSelectedStations.length - 1]?.name || 'Not selected';
		const isFormValid = this.state.modelId
			&& this.state.lotId
			&& this.state.jobQuantity >= 1
			&& selectedStations.length > 0
			&& selectedModelAutoTransactionType !== 'multiple'
			&& (!requiresAutoTransactionSerialGate || this.state.serialRequiredStationId);

		return (
			<div className="row">
				<div className="col-12 mb-3">
					<div style={jobPageStyles.heroCard}>
						<div className="row align-items-center">
							<div className="col-xl-8 col-lg-7">
								<div style={jobPageStyles.heroEyebrow}>ERP Jobs</div>
								<h2 style={jobPageStyles.heroTitle}>Build and Release a Production Job</h2>
								<div style={jobPageStyles.heroSubtitle}>
									Use ERP job setup to choose the model and lot, set quantity, assign stations, and apply item requirements before work starts.
								</div>
							</div>
							<div className="col-xl-4 col-lg-5 mt-3 mt-lg-0">
								<div style={jobPageStyles.heroStatRail}>
									<div style={jobPageStyles.heroStatItem}>
										<span style={jobPageStyles.heroStatLabel}>Readiness</span>
										<strong style={jobPageStyles.heroStatValue}>{readinessPercent}%</strong>
									</div>
									<div style={jobPageStyles.heroStatItem}>
										<span style={jobPageStyles.heroStatLabel}>Stations Selected</span>
										<strong style={jobPageStyles.heroStatValue}>{selectedStationCount} / {totalStationCount}</strong>
									</div>
									<div style={jobPageStyles.heroStatItem}>
										<span style={jobPageStyles.heroStatLabel}>Run Quantity</span>
										<strong style={jobPageStyles.heroStatValue}>{this.state.jobQuantity}</strong>
									</div>
										<MrpLocationControl
											rootState={this.props.rootState}
											id="mrp-job-location"
											wrapperStyle={{marginTop: '12px'}}
											selectStyle={jobPageStyles.heroSelect}
											note={LOCATION_NOTE}
										/>
									</div>
								</div>
						</div>
					</div>
				</div>
				<div className="col-xl-8 col-lg-12" style={jobPageStyles.mainColumn}>
					<div className="card" style={jobPageStyles.cardShell}>
						<div className="card-header" style={jobPageStyles.cardHeader}>
							<strong>Job Basics</strong>
						</div>
						<div className="card-body">
							<div className="form-row">
								<div className="form-group col-md-6">
									<label htmlFor="jobName">Job Name <span className="text-muted">(Optional)</span></label>
									<input
										id="jobName"
										name="jobName"
										type="text"
										className="form-control"
										value={this.state.jobName}
										onChange={this.handleTextChange}
										placeholder="Lot 2026-01 / Model X"
									/>
								</div>
								<div className="form-group col-md-6">
									<label htmlFor="modelId">Model <span className="text-danger">*</span></label>
									<div style={{display: "flex", alignItems: "center", gap: "5px"}}>
										{canCreateModels && (
											<>
												<Button
													onClick={this.handleShowModelModal}
													className={"select-add"}
													style={{minWidth: "30px", height: "30px", padding: "0"}}
													size={"sm"}
													outline
													color="primary"
													title="Add model"
												>
													<i className="fa fa-plus"/>
												</Button>
												<Button
													onClick={() => this.handleEditModelModal()}
													className={"select-add"}
													style={{minWidth: "30px", height: "30px", padding: "0"}}
													size={"sm"}
													outline
													color="info"
													title="Edit selected model"
													disabled={!this.state.modelId || this.state.loading}
												>
													<i className="fa fa-pencil"/>
												</Button>
												<Button
													onClick={() => this.handleDeleteModel()}
													className={"select-add"}
													style={{minWidth: "30px", height: "30px", padding: "0"}}
													size={"sm"}
													outline
													color="danger"
													title="Delete selected model"
													disabled={!this.state.modelId || this.state.loading}
												>
													<i className="fa fa-trash"/>
												</Button>
											</>
										)}
										<div style={{flexGrow: 1}}>
											<select
												id="modelId"
												name="modelId"
												className={`form-control ${this.state.validationErrors.modelId ? 'is-invalid' : ''}`}
												value={this.state.modelId}
												onChange={this.handleModelChange}
											>
												<option value="">Select a model</option>
												{this.state.models.map((model) => (
													<option key={model.id} value={model.id}>
														{model.name}
													</option>
												))}
											</select>
											{this.state.validationErrors.modelId && (
												<div className="invalid-feedback d-block">
													{this.state.validationErrors.modelId}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className="form-row">
								<div className="form-group col-md-6">
									<label htmlFor="lotId">Lot <span className="text-danger">*</span></label>
									<div style={{display: "flex", alignItems: "center", gap: "5px"}}>
										{canManageMrpDefinitions && (
											<Button
												onClick={this.handleShowLotModal}
												className={"select-add"}
												style={{minWidth: "30px", height: "30px", padding: "0"}}
												size={"sm"}
												outline
												color="primary"
											>
												<i className="fa fa-plus"/>
											</Button>
										)}
										<div style={{flexGrow: 1}}>
											<select
												id="lotId"
												name="lotId"
												className={`form-control ${this.state.validationErrors.lotId ? 'is-invalid' : ''}`}
												value={this.state.lotId}
												onChange={this.handleLotChange}
											>
												<option value="">Select a lot</option>
												{this.state.lots.map((lot) => (
													<option key={lot.id} value={lot.id}>
														{lot.lotNumber}
													</option>
												))}
											</select>
											{this.state.validationErrors.lotId && (
												<div className="invalid-feedback d-block">
													{this.state.validationErrors.lotId}
												</div>
											)}
										</div>
									</div>
								</div>
								<div className="form-group col-md-6">
									<label htmlFor="jobQuantity">Quantity (Models) <span className="text-danger">*</span></label>
									<input
										id="jobQuantity"
										name="jobQuantity"
										type="number"
										min="1"
										className={`form-control ${this.state.validationErrors.jobQuantity ? 'is-invalid' : ''}`}
										value={this.state.jobQuantity}
										onChange={this.handleNumberChange}
									/>
									<small className="form-text text-muted">
										Number of models produced for this job run.
									</small>
									{this.state.validationErrors.jobQuantity && (
										<div className="invalid-feedback d-block">
											{this.state.validationErrors.jobQuantity}
										</div>
									)}
								</div>
							</div>
							{this.state.validationErrors.stations && (
								<div className="alert alert-danger mb-0">
									{this.state.validationErrors.stations}
								</div>
							)}
							<div className="alert alert-info mb-0">
								All jobs are linked to a lot. Jobs can share lots, and lots can contain multiple jobs.
							</div>
						</div>
					</div>

						<div className="card" style={jobPageStyles.cardShell}>
							<div className="card-header" style={jobPageStyles.cardHeader}>
								<strong>Serial Number Requirement</strong>
							</div>
							<div className="card-body">
								<div className="form-group mb-0">
									<label htmlFor="serialRequiredStationId">
										Serial required at station {requiresAutoTransactionSerialGate ? <span className="text-danger">*</span> : <span className="text-muted">(Optional)</span>}
									</label>
									{requiresAutoTransactionSerialGate && (
										<div className="small text-muted mb-2">
											{selectedModelAutoTransactionType === 'transfer'
												? 'Transfer-enabled models must capture a serial at one ERP station before the built unit can be created and transferred from the current ERP location.'
												: 'Auto-create-enabled models must capture a serial at one ERP station before the built unit can be recorded in FFLBoss.'}
										</div>
									)}
									<div className="small text-muted mb-2">
										Manufacturer, caliber, type, and UPC are entered later in ERP Work at this same serial gate.
									</div>
									<select
										id="serialRequiredStationId"
										name="serialRequiredStationId"
										className={`form-control ${this.state.validationErrors.serialRequiredStationId ? 'is-invalid' : ''}`}
										value={this.state.serialRequiredStationId}
										onChange={this.handleTextChange}
										disabled={selectedStations.length === 0}
									>
										<option value="">Select a station</option>
										{selectedStations.map((station) => (
											<option key={station.id} value={station.id}>
												{station.name}
											</option>
										))}
									</select>
									{this.state.validationErrors.serialRequiredStationId && (
										<div className="invalid-feedback d-block">
											{this.state.validationErrors.serialRequiredStationId}
										</div>
									)}
								</div>
							</div>
						</div>

						<div className="card" style={jobPageStyles.cardShell}>
							<div className="card-header d-flex justify-content-between align-items-center" style={jobPageStyles.cardHeader}>
								<strong>Workflow Stations</strong>
							<Button
								color="primary"
								size="sm"
								onClick={() => this.props.history.push('/erp/stations')}
							>
								Manage Stations
								</Button>
							</div>
							<div className="card-body p-0">
								<div className="small text-muted px-3 pt-3">
									Selected order becomes the live workflow order for ERP Work and Tracker. Final-station completion drives unit output in tracker and reports.
								</div>
								<div className="table-responsive">
									<table className="table table-striped mb-0" style={jobPageStyles.stationTable}>
									<thead>
										<tr>
											<th style={{width: 60}}>Use</th>
											<th>Station</th>
											<th style={{width: 120}}>Order</th>
										</tr>
									</thead>
									<tbody>
										{this.state.stations.map((station, index) => (
											<tr key={station.id}>
												<td>
													<input
														type="checkbox"
														checked={station.selected}
														onChange={this.handleStationToggle(station.id)}
													/>
												</td>
												<td>{station.name}</td>
												<td>
													<div className="btn-group btn-group-sm" role="group">
														<button
															type="button"
															className="btn btn-light"
															onClick={this.moveStation(station.id, 'up')}
															disabled={!station.selected || index === 0}
														>
															<i className="fa fa-arrow-up"/>
														</button>
														<button
															type="button"
															className="btn btn-light"
															onClick={this.moveStation(station.id, 'down')}
															disabled={!station.selected || index === this.state.stations.length - 1}
														>
															<i className="fa fa-arrow-down"/>
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					{this.state.modelId && (
						<div className="card" style={jobPageStyles.cardShell}>
							<div className="card-header d-flex justify-content-between align-items-center" style={jobPageStyles.cardHeader}>
								<div className="d-flex align-items-center" style={{gap: '8px'}}>
									<strong>Model Requirements</strong>
									{hasModelOverrides && (
										<span style={requirementsStyles.editedBadge}>
											Edited
										</span>
									)}
								</div>
							</div>
							<div className="card-body">
								{this.state.modelLoadError && (
									<div className="alert alert-danger">
										{this.state.modelLoadError}
									</div>
								)}
								{!this.state.selectedModelDetails && !this.state.modelLoadError && (
									<div className="text-muted">
										Select a model to load requirements.
									</div>
								)}
								{this.state.selectedModelDetails && (
									<>
										<div style={requirementsStyles.summaryCard} className="mb-3">
											{canCreateModels && (
												<div className="d-flex justify-content-end mb-3" style={{gap: '8px'}}>
													<Button
														color="primary"
														size="sm"
														outline
														onClick={() => this.handleEditModelModal(this.state.selectedModelDetails)}
														disabled={this.state.loading}
													>
														<i className="fa fa-pencil"/> Edit Model
													</Button>
													<Button
														color="danger"
														size="sm"
														outline
														onClick={() => this.handleDeleteModel(this.state.selectedModelDetails)}
														disabled={this.state.loading}
													>
														<i className="fa fa-trash"/> Delete Model
													</Button>
												</div>
											)}
											<div className="row">
												<div className="col-md-7">
													<div style={requirementsStyles.sectionTitle}>Model</div>
													<div>
														<strong>{this.state.selectedModelDetails.name || 'Selected Model'}</strong>
													</div>
													{this.state.selectedModelDetails.sku && (
														<div className="text-muted">SKU: {this.state.selectedModelDetails.sku}</div>
													)}
													<div className="text-muted">
														Type: {this.isAssemblyModelType(this.state.selectedModelDetails.model_type) ? 'Assembly' : 'Firearm'}
													</div>
													{this.isAssemblyModelType(this.state.selectedModelDetails.model_type) && this.state.selectedModelDetails.assembly_output?.enabled && (
														<div className="text-muted">
															Output: {this.formatQuantity(this.state.selectedModelDetails.assembly_output.quantity_per_completed_unit || 1)} per completed unit to {this.state.selectedModelDetails.assembly_output.station_name || 'selected station'}
														</div>
													)}
													{this.state.selectedModelDetails.description && (
														<div className="text-muted">{this.state.selectedModelDetails.description}</div>
													)}
												</div>
												<div className="col-md-5">
													<div className="d-flex justify-content-between">
														<span>Stations in model</span>
														<strong>{modelStations.length}</strong>
													</div>
													<div className="d-flex justify-content-between">
														<span>Unique items</span>
														<strong>{modelPartsSummary.length}</strong>
													</div>
													<div className="d-flex justify-content-between">
														<span>Total quantity</span>
														<strong>{this.formatQuantity(modelPartsTotal)}</strong>
													</div>
													<div className="d-flex justify-content-between">
														<span>Max units with current QoH</span>
														<strong>{hasModelInventoryCapacity ? maxAvailableUnits : 'N/A'}</strong>
													</div>
												</div>
											</div>
											{hasModelInventoryCapacity ? (
												<div style={requirementsStyles.capacityNote}>
													Current ERP on-hand quantities support up to <strong>{maxAvailableUnits}</strong> unit{maxAvailableUnits === 1 ? '' : 's'} of this model at the selected location.
													{limitingPartNames.length > 0 ? ` Bottleneck: ${limitingPartNames.join(', ')}.` : ''}
												</div>
											) : (
												<div style={requirementsStyles.capacityNote}>
													Add tracked item requirements to this model to calculate an inventory-based production ceiling.
												</div>
											)}
										</div>

										{exceedsAvailableUnits && (
											<div className="alert alert-warning">
												Run quantity is set to {this.state.jobQuantity}, but current item stock only supports {maxAvailableUnits} completed unit{maxAvailableUnits === 1 ? '' : 's'} for this model at the selected ERP location.
											</div>
										)}

										{missingModelStations.length > 0 && (
											<div className="alert alert-warning">
												Some stations from this model are not available:&nbsp;
												{missingModelStations.map((station) => (
													station.station_name || `Station ${station.station_id}`
												)).join(', ')}
											</div>
										)}

										<div style={requirementsStyles.sectionCard} className="mb-3">
											<div style={requirementsStyles.sectionHeader}>
												<div style={requirementsStyles.sectionTitle}>Job Overrides</div>
												<div style={requirementsStyles.sectionMeta}>Does not update the model</div>
											</div>
											<div className="d-flex justify-content-between align-items-center mb-2">
												<div className="text-muted" style={{fontSize: '13px'}}>
													Adjust stations, items, and quantities for this job only.
												</div>
												<div className="btn-group btn-group-sm" role="group">
													<button
														type="button"
														className="btn btn-outline-secondary"
														onClick={this.handleToggleEditModelRequirements}
													>
														{isEditingModelRequirements ? 'Hide Editor' : 'Edit Requirements'}
													</button>
													<button
														type="button"
														className="btn btn-outline-secondary"
														onClick={this.resetJobModelToDefaults}
														disabled={this.state.selectedModelStations.length === 0}
													>
														Reset to Model
													</button>
												</div>
											</div>

											{isEditingModelRequirements && (
												<div>
													<div style={requirementsStyles.addRow} className="mb-3">
														<select
															className="form-control form-control-sm"
															style={{maxWidth: '260px'}}
															value={this.state.jobModelStationToAdd}
															onChange={(e) => this.setState({jobModelStationToAdd: e.target.value})}
														>
															<option value="">Add station...</option>
															{availableJobModelStations.map((station) => (
																<option key={station.id} value={station.id}>
																	{station.name}
																</option>
															))}
														</select>
														<button
															type="button"
															className="btn btn-sm btn-outline-primary"
															onClick={this.handleJobModelStationAdd}
															disabled={!this.state.jobModelStationToAdd}
														>
															Add Station
														</button>
													</div>

													{modelStations.length === 0 ? (
														<div className="text-muted">No stations configured for this job.</div>
													) : (
														modelStations.map((station, index) => {
															const stationName = station.station_name || this.getStationNameById(station.station_id);
															const stationData = this.state.stations.find((entry) => entry.id === station.station_id);
															const stationParts = Array.isArray(stationData?.parts) ? stationData.parts : [];
															const availableParts = stationParts.filter((part) => {
																const partKey = `${part.pkey || part.id || part.name}`;
																return !station.parts.some((stationPart) => `${stationPart.part_id}` === partKey);
															});
															const partSelectionValue = this.state.jobModelPartSelections[station.station_id] || '';
															return (
																<div key={station.station_id || station.station_name || index} style={requirementsStyles.editStationCard}>
																	<div style={requirementsStyles.editStationHeader}>
																		<div>
																			<strong>{index + 1}. {stationName}</strong>
																		</div>
																		<div style={requirementsStyles.editActions}>
																			<button
																				type="button"
																				className="btn btn-light btn-sm"
																				onClick={this.handleJobModelStationMove(station.station_id, 'up')}
																				disabled={index === 0}
																				title="Move up"
																			>
																				<i className="fa fa-arrow-up"/>
																			</button>
																			<button
																				type="button"
																				className="btn btn-light btn-sm"
																				onClick={this.handleJobModelStationMove(station.station_id, 'down')}
																				disabled={index === modelStations.length - 1}
																				title="Move down"
																			>
																				<i className="fa fa-arrow-down"/>
																			</button>
																			<button
																				type="button"
																				className="btn btn-outline-danger btn-sm"
																				onClick={() => this.handleJobModelStationRemove(station.station_id)}
																			>
																				Remove Station
																			</button>
																		</div>
																	</div>
																	{station.parts.length === 0 ? (
																	<div className="text-muted">No items listed for this station.</div>
																) : (
																	<div className="table-responsive" style={requirementsStyles.tableWrapper}>
																		<table className="table table-sm table-bordered mb-0">
																			<thead>
																				<tr>
																					<th>Item</th>
																					<th style={{width: 160}}>Required Qty (Unit)</th>
																					<th style={{width: 120}}>Actions</th>
																					</tr>
																				</thead>
																				<tbody>
																					{station.parts.map((part, partIndex) => (
																						<tr key={part.part_id || part.part_name || partIndex}>
																							<td>{part.part_name || part.part_id}</td>
																							<td>
																								<input
																									type="number"
																									min="0"
																									step="1"
																									className="form-control form-control-sm"
																									value={part.quantity ?? ''}
																									onChange={this.handleJobModelPartQuantityChange(station.station_id, part.part_id)}
																									placeholder="0"
																								/>
																							</td>
																							<td>
																								<button
																									type="button"
																									className="btn btn-sm btn-outline-danger"
																									onClick={() => this.handleJobModelPartRemove(station.station_id, part.part_id)}
																								>
																									Remove
																								</button>
																							</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		</div>
																	)}
																	<div style={requirementsStyles.addRow}>
																		<select
																			className="form-control form-control-sm"
																			style={{maxWidth: '260px'}}
																			value={partSelectionValue}
																			onChange={this.handleJobModelPartSelectionChange(station.station_id)}
																		>
																			<option value="">Add item...</option>
																			{availableParts.map((part) => {
																				const partKey = part.pkey || part.id || part.name;
																				return (
																					<option key={partKey} value={partKey}>
																						{part.name || partKey}
																					</option>
																				);
																			})}
																		</select>
																		<button
																			type="button"
																			className="btn btn-sm btn-outline-primary"
																			onClick={() => this.handleJobModelPartAdd(station.station_id)}
																			disabled={!partSelectionValue}
																		>
																			Add Item
																		</button>
																	</div>
																</div>
															);
														})
													)}
												</div>
											)}
										</div>

										<div style={requirementsStyles.sectionCard} className="mb-3">
											<div style={requirementsStyles.sectionHeader}>
												<div style={requirementsStyles.sectionTitle}>Items Summary</div>
												<div style={requirementsStyles.sectionMeta}>
													{modelPartsSummary.length} items
												</div>
											</div>
											{modelPartsSummary.length === 0 ? (
												<div className="text-muted mt-2">No items listed for this model.</div>
											) : (
												<div className="table-responsive mt-2" style={requirementsStyles.tableWrapper}>
													<table className="table table-sm table-bordered mb-0">
														<thead>
															<tr>
																<th>Item</th>
																<th style={{width: 140}}>Total Qty</th>
																<th style={{width: 140}}>On Hand</th>
																<th style={{width: 140}}>Max Units</th>
																<th>Used At</th>
															</tr>
														</thead>
														<tbody>
															{modelPartsSummary.map((part) => (
																<tr key={part.part_id}>
																	<td>{part.part_name}</td>
																	<td>{this.formatQuantity(part.total_quantity)}</td>
																	<td>{this.formatQuantity(part.quantity_on_hand)}</td>
																	<td>{part.max_units === null ? 'N/A' : part.max_units}</td>
																	<td>
																		{part.stations.map((station) => (
																			`${station.station_name} (${this.formatQuantity(station.quantity)})`
																		)).join(', ')}
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											)}
										</div>

										<div style={requirementsStyles.sectionCard}>
											<div style={requirementsStyles.sectionHeader}>
												<div style={requirementsStyles.sectionTitle}>Items by Station</div>
												<div style={requirementsStyles.sectionMeta}>
													{modelStations.length} stations
												</div>
											</div>
											{modelStations.length === 0 ? (
												<div className="text-muted mt-2">No stations configured for this model.</div>
											) : (
												modelStations.map((station, index) => {
													const stationName = station.station_name || this.getStationNameById(station.station_id);
													const parts = Array.isArray(station.parts) ? station.parts : [];
													return (
														<div key={station.station_id || station.station_name || index} style={requirementsStyles.stationCard}>
															<div style={requirementsStyles.stationHeader}>
																<div>
																	<strong>{index + 1}. {stationName}</strong>
																</div>
															<span style={requirementsStyles.pill}>
																{parts.length} items
															</span>
															</div>
															{parts.length === 0 ? (
																<div className="text-muted">No items listed for this station.</div>
															) : (
																<div className="table-responsive" style={requirementsStyles.tableWrapper}>
																	<table className="table table-sm table-bordered mb-0">
																		<thead>
																			<tr>
																				<th>Item</th>
																				<th style={{width: 160}}>Required Qty (Unit)</th>
																			</tr>
																		</thead>
																		<tbody>
																			{parts.map((part, partIndex) => (
																				<tr key={part.part_id || part.part_name || partIndex}>
																					<td>{part.part_name || part.part_id}</td>
																					<td>{this.formatQuantity(part.quantity)}</td>
																				</tr>
																			))}
																		</tbody>
																	</table>
																</div>
															)}
														</div>
													);
												})
											)}
										</div>
									</>
								)}
							</div>
						</div>
					)}

						<div className="card" style={jobPageStyles.cardShell}>
							<div className="card-body">
								<div className="d-flex justify-content-end gap-2">
								<Button
									color="primary"
									onClick={this.createJob}
									disabled={this.state.loading || !isFormValid}
								>
									{this.state.loading ? <i className="fa fa-spinner fa-spin"/> : null}
									{' '}Create Job
									</Button>
								</div>
							</div>
						</div>
				</div>

				<div className="col-xl-4 col-lg-12">
					<div className="card" style={{...jobPageStyles.cardShell, ...jobPageStyles.opsCard}}>
						<div className="card-body">
							<div style={jobPageStyles.opsHeaderRow}>
								<div style={jobPageStyles.opsTitle}>Launch Readiness</div>
								<div style={jobPageStyles.opsPercent}>{readinessPercent}%</div>
							</div>
							<div style={jobPageStyles.progressTrack}>
								<div style={{...jobPageStyles.progressFill, width: `${readinessPercent}%`}}/>
							</div>
							<div style={jobPageStyles.checklistWrap}>
								{readinessChecks.map((check) => (
									<div key={check.label} style={jobPageStyles.checkItem}>
										<span style={check.done ? jobPageStyles.checkDone : jobPageStyles.checkPending}>
											{check.done ? <i className="fa fa-check"/> : <i className="fa fa-circle-o"/>}
										</span>
										<span>{check.label}</span>
									</div>
								))}
							</div>
							<div style={jobPageStyles.runtimeHint}>
								Station labor and item time are tracked live during execution in ERP Work.
							</div>
						</div>
					</div>
					<div className="card mt-3" style={{...jobPageStyles.cardShell, ...jobPageStyles.contextCard}}>
						<div className="card-body">
							<div style={jobPageStyles.sidebarSectionTitle}>Workflow Snapshot</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Coverage</span>
								<strong>{stationCoveragePercent}%</strong>
							</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>First station</span>
								<strong style={jobPageStyles.metricValueCompact}>{firstStationName}</strong>
							</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Final station</span>
								<strong style={jobPageStyles.metricValueCompact}>{finalStationName}</strong>
							</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Serial gate</span>
								<strong style={jobPageStyles.metricValueCompact}>
									{this.state.serialRequiredStationId ? this.getStationNameById(this.state.serialRequiredStationId) : (requiresAutoTransactionSerialGate ? 'Required' : 'Not required')}
								</strong>
							</div>
						</div>
					</div>
					<div className="card mt-3" style={{...jobPageStyles.cardShell, ...jobPageStyles.contextCard}}>
						<div className="card-body">
							<div style={jobPageStyles.sidebarSectionTitle}>Job Profile</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Model</span>
								<strong style={jobPageStyles.metricValueCompact}>
									{this.state.selectedModelDetails?.name || 'Not selected'}
								</strong>
							</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Type</span>
								<strong>{this.state.modelId ? modelTypeLabel : '-'}</strong>
							</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Lot</span>
								<strong style={jobPageStyles.metricValueCompact}>
									{selectedLot?.lotNumber || 'Not selected'}
								</strong>
							</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Units</span>
								<strong>{this.state.jobQuantity}</strong>
							</div>
							<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
								<span>Auto Create</span>
								<strong>{selectedModelAutoTransactionLabel}</strong>
							</div>
							{selectedModelAutoTransactionType === 'multiple' && (
								<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
									<span>Status</span>
									<strong style={{...jobPageStyles.metricValueCompact, color: '#c0392b'}}>Fix model setup</strong>
								</div>
							)}
							{selectedModelAutoTransactionType === 'transfer' && (
								<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
									<span>Dealer</span>
									<strong style={jobPageStyles.metricValueCompact}>{selectedModelAutoTransactionConfig.dealer_name || 'Selected dealer'}</strong>
								</div>
							)}
							{selectedModelAutoTransactionType === 'acquisition' && (
								<div className="d-flex justify-content-between" style={jobPageStyles.metricRow}>
									<span>Source</span>
									<strong style={jobPageStyles.metricValueCompact}>Self / Internal</strong>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Model Add Modal */}
				{canCreateModels && this.state.showModelModal && (
					<div
						style={modalStyles.modalOverlay}
						onMouseDown={this.handleModalOverlayMouseDown}
						onClick={this.handleModalOverlayClick(this.handleCloseModelModal)}
					>
						<div 
							style={{...modalStyles.modalContent, ...modalStyles.largeModalContent}} 
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !e.shiftKey) {
									e.preventDefault();
									if (this.state.modelStep === 1) {
										this.handleModelNextStep();
									} else {
										this.handleSaveModel();
									}
								}
							}}
							tabIndex={-1}
						>
							<h2 style={modalStyles.heading}>{isEditingModelModal ? 'Edit Model' : 'Add New Model'}</h2>
							<div style={modalStyles.stepBadge}>
								Step {this.state.modelStep} of 2
							</div>

							{this.state.modelStep === 1 && (
								<div>
									<div style={modalStyles.inputGroup}>
										<label htmlFor="newModelName" style={modalStyles.label}>
											Model Name:
										</label>
										<input
											id="newModelName"
											type="text"
											value={this.state.newModelName}
											onChange={(e) => this.setState({newModelName: e.target.value, modelError: ''})}
											onKeyDown={(e) => {
												if (e.key === 'Enter' && !e.shiftKey) {
													e.preventDefault();
													this.handleModelNextStep();
												}
											}}
											style={modalStyles.inputField}
											autoFocus
										/>
									</div>
									<div style={modalStyles.inputGroup}>
										<label htmlFor="newModelType" style={modalStyles.label}>
											Model Type:
										</label>
										<select
											id="newModelType"
											value={this.state.newModelType}
											onChange={(e) => this.setState({
												newModelType: e.target.value,
												newModelAssemblyOutput: e.target.value === 'assembly'
													? this.state.newModelAssemblyOutput
													: this.getEmptyModelAssemblyOutputConfig(),
												newModelAcquisition: e.target.value === 'assembly'
													? this.getEmptyModelAcquisitionConfig()
													: this.state.newModelAcquisition,
												newModelTransfer: e.target.value === 'assembly'
													? this.getEmptyModelTransferConfig()
													: this.state.newModelTransfer,
												modelError: ''
											})}
											style={modalStyles.inputField}
										>
											<option value="firearm">Firearm</option>
											<option value="assembly">Assembly (in-house)</option>
										</select>
									</div>
									{this.state.newModelType === 'assembly' && (
										<div style={{...modalStyles.section, marginTop: '18px'}}>
											<div className="d-flex align-items-center justify-content-between flex-wrap">
												<div>
													<div style={modalStyles.sectionTitle}>Completed Assembly Output</div>
													<div style={modalStyles.inlineHelp}>
														When units finish at the final workflow station, ERP can add this assembly as an item on another station.
													</div>
												</div>
												<label className="mb-0 d-flex align-items-center" style={{gap: '8px', fontWeight: 600}}>
													<input
														type="checkbox"
														checked={!!this.state.newModelAssemblyOutput?.enabled}
														onChange={this.handleNewModelAssemblyOutputToggle}
													/>
													Add completed assembly to station
												</label>
											</div>
											{this.state.newModelAssemblyOutput?.enabled && (
												<div className="mt-3">
													<div className="row">
														<div className="col-md-6">
															<div style={modalStyles.inputGroup}>
																<label htmlFor="newModelAssemblyOutputStation" style={modalStyles.label}>
																	Output Station
																</label>
																<select
																	id="newModelAssemblyOutputStation"
																	value={this.state.newModelAssemblyOutput?.station_id || ''}
																	onChange={this.handleNewModelAssemblyOutputStationChange}
																	style={modalStyles.inputField}
																>
																	<option value="">Select a station</option>
																	{this.state.stations.map((station) => (
																		<option key={station.id} value={station.id}>
																			{station.name}
																		</option>
																	))}
																</select>
															</div>
														</div>
														<div className="col-md-6">
															<div style={modalStyles.inputGroup}>
																<label htmlFor="newModelAssemblyOutputQuantity" style={modalStyles.label}>
																	Output Qty Per Completed Unit
																</label>
																<input
																	id="newModelAssemblyOutputQuantity"
																	type="number"
																	min="0.01"
																	step="0.01"
																	value={this.state.newModelAssemblyOutput?.quantity_per_completed_unit || '1'}
																	onChange={this.handleNewModelAssemblyOutputQuantityChange}
																	style={modalStyles.inputField}
																	placeholder="1"
																/>
															</div>
														</div>
													</div>
													<div className="small text-muted mt-2">
														Example: if one completed CNC block yields 6 lowers, set this to 6 so 5 completed units post 30 lowers to the output station.
													</div>
												</div>
											)}
										</div>
									)}
									{this.state.newModelType !== 'assembly' && this.renderModelTransactionSection({
										transactionType: 'acquisition',
										title: 'Bound Book Acquisition',
										helpText: 'Enable this when completed ERP units should create a product and acquisition entry from self/internal at the active ERP location.',
										toggleLabel: 'Add to acquisition / bound book',
										dealerLabel: 'Acquired From Dealer'
									})}
									{this.state.newModelType !== 'assembly' && this.renderModelTransactionSection({
										transactionType: 'transfer',
										title: 'Dealer Transfer',
										helpText: 'Enable this when completed ERP units should create a product and transfer it to a dealer.',
										toggleLabel: 'Create transfer to dealer',
										dealerLabel: 'Transfer To Dealer',
										locationNote: 'The active ERP location is used as the transfer-from location when the unit is completed.'
									})}
									<div style={modalStyles.section}>
										<div style={modalStyles.sectionTitle}>Stations for this model</div>
										<div className="row">
											<div className="col-md-6">
												<div style={modalStyles.subtleLabel}>Available Stations</div>
												<div style={modalStyles.listBox}>
													{availableModelStations.length === 0 ? (
														<div style={modalStyles.emptyState}>
															All stations are selected.
														</div>
													) : (
														availableModelStations.map((station) => (
															<div key={station.id} style={modalStyles.listItem}>
																<div style={modalStyles.listItemText}>
																	<strong>{station.name}</strong>
																</div>
																<button
																	style={{...modalStyles.actionButton, ...modalStyles.actionPrimary}}
																	onClick={() => this.handleAddModelStation(station.id)}
																>
																	<i className="fa fa-plus"/> Add
																</button>
															</div>
														))
													)}
												</div>
											</div>
											<div className="col-md-6">
												<div style={modalStyles.subtleLabel}>Selected Stations (in order)</div>
												<div style={modalStyles.listBox}>
													{newModelStations.length === 0 ? (
														<div style={modalStyles.emptyState}>
															Select stations to define the workflow.
														</div>
													) : (
														newModelStations.map((station, index) => (
															<div key={station.id} style={modalStyles.listItem}>
																<div style={modalStyles.listItemText}>
																	<strong>{index + 1}. {station.name}</strong>
																</div>
																<div style={modalStyles.listItemActions}>
																	<button
																		style={modalStyles.iconButton}
																		onClick={this.moveModelStation(station.id, 'up')}
																		disabled={index === 0}
																		title="Move up"
																	>
																		<i className="fa fa-arrow-up"/>
																	</button>
																	<button
																		style={modalStyles.iconButton}
																		onClick={this.moveModelStation(station.id, 'down')}
																		disabled={index === newModelStations.length - 1}
																		title="Move down"
																	>
																		<i className="fa fa-arrow-down"/>
																	</button>
																	<button
																		style={{...modalStyles.actionButton, ...modalStyles.actionDanger}}
																		onClick={() => this.handleRemoveModelStation(station.id)}
																	>
																		Remove
																	</button>
																</div>
															</div>
														))
													)}
												</div>
											</div>
										</div>
											<div style={modalStyles.inlineHelp}>
												Select the stations and order to build this model.
											</div>
											<div style={modalStyles.inlineHelp}>
												The last station in this list is treated as the completion point for unit-output tracking.
											</div>
										</div>
									</div>
								)}

							{this.state.modelStep === 2 && (
								<div>
									<div style={modalStyles.inlineHelp}>
										Click <strong>+</strong> to add items required at each station. Adjust quantities as needed.
									</div>
									{newModelStations.length === 0 ? (
										<div style={modalStyles.emptyState}>No stations selected.</div>
									) : (
										newModelStations.map((station, index) => {
											const selectedPartsCount = this.getSelectedModelParts(station).length;
											const qohTooltipId = `qoh-tooltip-${station.id}`;
											const newItemDraft = this.getModelItemDraft(station.id);
											const isCreatingStationItem = `${this.state.creatingModelPartStationId}` === `${station.id}`;
											return (
												<div key={station.id} style={modalStyles.stationCard}>
													<div style={modalStyles.stationCardHeader}>
														<div>
															<div style={modalStyles.stationTitle}>
																{index + 1}. {station.name}
															</div>
														<div style={modalStyles.stationMeta}>
															{(station.parts || []).length} available items
															</div>
														</div>
														<div style={modalStyles.countPill}>
															{selectedPartsCount} selected
														</div>
													</div>
													<div style={modalStyles.inlineItemForm}>
														<div className="form-row">
															<div className="form-group col-md-2 mb-2">
																<label style={modalStyles.inlineFieldLabel}>QoH</label>
																<input
																	type="number"
																	min="0"
																	step="1"
																	className="form-control form-control-sm"
																	value={newItemDraft.qoh}
																	onChange={this.handleModelItemDraftChange(station.id, 'qoh')}
																	placeholder="0"
																/>
															</div>
															<div className="form-group col-md-3 mb-2">
																<label style={modalStyles.inlineFieldLabel}>Item</label>
																<input
																	type="text"
																	className="form-control form-control-sm"
																	value={newItemDraft.item}
																	onChange={this.handleModelItemDraftChange(station.id, 'item')}
																	placeholder="Receiver"
																/>
															</div>
															<div className="form-group col-md-4 mb-2">
																<label style={modalStyles.inlineFieldLabel}>Description</label>
																<input
																	type="text"
																	className="form-control form-control-sm"
																	value={newItemDraft.description}
																	onChange={this.handleModelItemDraftChange(station.id, 'description')}
																	placeholder="Optional"
																/>
															</div>
															<div className="form-group col-md-2 mb-2">
																<label style={modalStyles.inlineFieldLabel}>Required Qty</label>
																<input
																	type="number"
																	min="0"
																	step="1"
																	className="form-control form-control-sm"
																	value={newItemDraft.requiredQty}
																	onChange={this.handleModelItemDraftChange(station.id, 'requiredQty')}
																	placeholder="1"
																/>
															</div>
															<div className="form-group col-md-1 mb-2 d-flex align-items-end">
																<button
																	type="button"
																	className="btn btn-sm btn-outline-primary w-100"
																	onClick={() => this.handleCreateModelStationItem(station)}
																	disabled={isCreatingStationItem}
																	title="Add new item line"
																>
																	{isCreatingStationItem ? <i className="fa fa-spinner fa-spin"/> : <i className="fa fa-plus"/>}
																</button>
															</div>
														</div>
													</div>
												{Array.isArray(station.parts) && station.parts.length > 0 ? (
													<div className="table-responsive">
														<table className="table table-sm table-bordered mb-0">
															<thead>
																<tr>
																	<th style={{width: 90}}>
																		<span
																			id={qohTooltipId}
																			style={{borderBottom: '1px dashed #999'}}
																		>
																			QoH
																		</span>
																		<UncontrolledTooltip placement="top" target={qohTooltipId}>
																			Quantity on Hand
																		</UncontrolledTooltip>
																	</th>
																	<th>Item</th>
																	<th>Description</th>
																	<th style={{width: 200}}>Required Qty (Unit)</th>
																</tr>
															</thead>
															<tbody>
																{station.parts.map((part) => {
																	const partKey = part.pkey || part.id || part.name;
																	const stationSelections = this.state.newModelStationParts[station.id] || {};
																	const quantityValue = stationSelections[partKey] ?? '';
																	const isAdded = quantityValue !== '' && quantityValue !== undefined && quantityValue !== null;
																	return (
																		<tr key={partKey}>
																			<td>{part.quantity || '-'}</td>
																			<td>{part.name}</td>
																			<td>{part.description || '-'}</td>
																			<td>
																				{isAdded ? (
																					<div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
																						<input
																							type="number"
																							min="0"
																							step="1"
																							className="form-control form-control-sm"
																							value={quantityValue}
																							onChange={this.handleModelPartQuantityChange(station.id, partKey)}
																							style={{flex: 1}}
																						/>
																						<button
																							type="button"
																							className="btn btn-sm btn-outline-danger"
																							onClick={() => this.setState((prevState) => ({
																								newModelStationParts: {
																									...prevState.newModelStationParts,
																									[station.id]: {
																										...(prevState.newModelStationParts[station.id] || {}),
																										[partKey]: ''
																									}
																								}
																							}))}
																							title="Remove item"
																							style={{padding: '2px 6px', lineHeight: 1}}
																						>
																							<i className="fa fa-times"/>
																						</button>
																					</div>
																				) : (
																					<button
																						type="button"
																						className="btn btn-sm btn-outline-primary"
																						onClick={() => this.setState((prevState) => ({
																							newModelStationParts: {
																								...prevState.newModelStationParts,
																								[station.id]: {
																									...(prevState.newModelStationParts[station.id] || {}),
																									[partKey]: '1'
																								}
																							}
																						}))}
																						title="Add this item"
																					>
																						<i className="fa fa-plus"/> Add
																					</button>
																				)}
																			</td>
																		</tr>
																	);
																})}
															</tbody>
														</table>
													</div>
													) : (
													<div style={modalStyles.emptyState}>
														No items configured for this station.
															<div style={{marginTop: '8px'}}>
																<button
																	style={{...modalStyles.actionButton, ...modalStyles.actionPrimary}}
																	onClick={() => {
																		this.handleCloseModelModal();
																		this.props.history.push('/erp/stations');
																	}}
																>
																	Manage Stations
																</button>
															</div>
														</div>
													)}
												</div>
											);
										})
									)}
								</div>
							)}

							{this.state.modelError && <p style={modalStyles.errorText}>{this.state.modelError}</p>}
							<div style={modalStyles.buttonGroup}>
								<button
									style={modalStyles.cancelButton}
									onClick={this.handleCloseModelModal}
									disabled={this.state.loading}
								>
									Cancel
								</button>
								{this.state.modelStep === 1 ? (
									<button
										style={modalStyles.saveButton}
										onClick={this.handleModelNextStep}
										disabled={this.state.loading}
									>
										Next
									</button>
								) : (
									<>
										<button
											style={modalStyles.secondaryButton}
											onClick={this.handleModelPrevStep}
											disabled={this.state.loading}
										>
											Back
										</button>
										<button
											style={modalStyles.saveButton}
											onClick={this.handleSaveModel}
											disabled={this.state.loading}
										>
											{this.state.loading ? <i className="fa fa-spinner fa-spin"/> : null}
											{' '}{isEditingModelModal ? 'Save Changes' : 'Save Model'}
										</button>
									</>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Lot Add Modal */}
				{canManageMrpDefinitions && this.state.showLotModal && (
					<div
						style={modalStyles.modalOverlay}
						onMouseDown={this.handleModalOverlayMouseDown}
						onClick={this.handleModalOverlayClick(this.handleCloseLotModal)}
					>
						<div 
							style={modalStyles.modalContent} 
							onClick={(e) => e.stopPropagation()}
						>
							<h2 style={modalStyles.heading}>Add New Lot</h2>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="newLotNumber" style={modalStyles.label}>
									Lot Number:
								</label>
								<input
									id="newLotNumber"
									type="text"
									value={this.state.newLotNumber}
									onChange={(e) => this.setState({newLotNumber: e.target.value, lotError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSaveLot();
										}
									}}
									style={modalStyles.inputField}
									autoFocus
								/>
								{this.state.lotError && <p style={modalStyles.errorText}>{this.state.lotError}</p>}
							</div>
							<div style={modalStyles.buttonGroup}>
								<button
									style={modalStyles.cancelButton}
									onClick={this.handleCloseLotModal}
									disabled={this.state.loading}
								>
									Cancel
								</button>
								<button
									style={modalStyles.saveButton}
									onClick={this.handleSaveLot}
									disabled={this.state.loading}
								>
									{this.state.loading ? <i className="fa fa-spinner fa-spin"/> : null}
									{' '}Save
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}
}

const jobPageStyles = {
	heroCard: {
		background: 'linear-gradient(135deg, #eef3ff 0%, #fafcff 45%, #e6f2fb 100%)',
		border: '1px solid #d6e2f0',
		borderTop: '4px solid #356fd1',
		borderRadius: '14px',
		padding: '22px 24px',
		color: '#17324d',
		boxShadow: '0 10px 24px rgba(12, 55, 92, 0.08)',
	},
	heroEyebrow: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '4px 10px',
		borderRadius: '999px',
		backgroundColor: '#dfe9ff',
		color: '#255cae',
		fontSize: '11px',
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		fontWeight: 700,
	},
	heroTitle: {
		fontSize: '27px',
		lineHeight: 1.2,
		margin: '6px 0 8px',
		fontWeight: 700,
	},
	heroSubtitle: {
		fontSize: '14px',
		color: '#557086',
		maxWidth: '520px',
	},
	heroStatRail: {
		background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, #eef3ff 100%)',
		border: '1px solid #d7e4f2',
		borderRadius: '12px',
		padding: '12px 14px',
		boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.78)',
	},
	heroSelect: {
		backgroundColor: '#ffffff',
		border: '1px solid #c9d9e8',
		color: '#17324d',
	},
	heroStatItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '7px 0',
		borderBottom: '1px solid #e3ecf5',
		gap: '8px',
	},
	heroStatLabel: {
		fontSize: '12px',
		color: '#5f7489',
	},
	heroStatValue: {
		fontSize: '15px',
		color: '#17324d',
		fontWeight: 700,
	},
	cardShell: {
		borderRadius: '12px',
		border: '1px solid #d6dfe9',
		boxShadow: '0 6px 16px rgba(14, 44, 80, 0.08)',
		overflow: 'hidden',
	},
	mainColumn: {
		display: 'grid',
		rowGap: '16px',
	},
	cardHeader: {
		background: 'linear-gradient(90deg, #f8fbff 0%, #edf5ff 100%)',
		borderBottom: '1px solid #d6e4f5',
	},
	stationTable: {
		marginBottom: 0,
	},
	opsCard: {
		background: 'linear-gradient(180deg, #fffef8 0%, #fff6dd 100%)',
		borderColor: '#f2ddb0',
	},
	opsHeaderRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '8px',
	},
	opsTitle: {
		fontSize: '15px',
		fontWeight: 700,
		color: '#5f4300',
	},
	opsPercent: {
		backgroundColor: '#f08b00',
		color: '#fff',
		padding: '3px 10px',
		borderRadius: '999px',
		fontWeight: 700,
		fontSize: '12px',
	},
	progressTrack: {
		height: '8px',
		borderRadius: '999px',
		backgroundColor: '#f2dfb8',
		overflow: 'hidden',
		marginBottom: '12px',
	},
	progressFill: {
		height: '100%',
		background: 'linear-gradient(90deg, #ff8a00 0%, #ff5f6d 100%)',
	},
	checklistWrap: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		rowGap: '6px',
	},
	checkItem: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		fontSize: '13px',
		color: '#524119',
	},
	checkDone: {
		color: '#1f9a47',
	},
	checkPending: {
		color: '#966f1f',
	},
	runtimeHint: {
		marginTop: '12px',
		padding: '10px',
		backgroundColor: '#fffdf6',
		border: '1px solid #f4e3bc',
		borderRadius: '8px',
		fontSize: '12px',
		color: '#695524',
	},
	contextCard: {
		backgroundColor: '#f9fcff',
	},
	sidebarSectionTitle: {
		fontSize: '13px',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.06em',
		color: '#24507b',
		marginBottom: '10px',
	},
	metricRow: {
		fontSize: '13px',
		padding: '7px 0',
		borderBottom: '1px dashed #d4e3f2',
	},
	metricValueCompact: {
		maxWidth: '165px',
		textAlign: 'right',
	},
};

const modalStyles = {
	modalOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1000,
	},
	modalContent: {
		backgroundColor: 'white',
		padding: '25px',
		borderRadius: '8px',
		width: '400px',
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
	},
	largeModalContent: {
		width: '820px',
		maxWidth: '95vw',
		maxHeight: '90vh',
		overflowY: 'auto',
	},
	heading: {
		fontSize: '24px',
		fontWeight: '600',
		marginTop: 0,
		marginBottom: '20px',
		color: '#333',
		borderBottom: '1px solid #eee',
		paddingBottom: '10px',
	},
	stepBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		backgroundColor: '#eef5ff',
		color: '#1f5cc4',
		padding: '4px 12px',
		borderRadius: '999px',
		fontSize: '12px',
		fontWeight: '600',
		marginBottom: '16px',
	},
	inputGroup: {
		marginBottom: '25px',
	},
	label: {
		display: 'block',
		marginBottom: '8px',
		fontWeight: '500',
		color: '#555',
		fontSize: '16px',
	},
	inputField: {
		width: '100%',
		padding: '10px 12px',
		border: '1px solid #ddd',
		borderRadius: '4px',
		fontSize: '16px',
		boxSizing: 'border-box',
		transition: 'border-color 0.2s ease',
		outline: 'none',
	},
	buttonGroup: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '12px',
	},
	secondaryButton: {
		padding: '10px 18px',
		backgroundColor: '#fff',
		color: '#333',
		border: '1px solid #ddd',
		borderRadius: '4px',
		cursor: 'pointer',
		fontWeight: '500',
		transition: 'background-color 0.2s ease',
	},
	cancelButton: {
		padding: '10px 18px',
		backgroundColor: '#f1f1f1',
		color: '#333',
		border: 'none',
		borderRadius: '4px',
		cursor: 'pointer',
		fontWeight: '500',
		transition: 'background-color 0.2s ease',
	},
	saveButton: {
		padding: '10px 18px',
		backgroundColor: '#007bff',
		color: 'white',
		border: 'none',
		borderRadius: '4px',
		cursor: 'pointer',
		fontWeight: '500',
		transition: 'background-color 0.2s ease',
	},
	errorText: {
		color: '#dc3545',
		margin: '8px 0 0 0',
		fontSize: '14px',
		fontWeight: '500',
	},
	section: {
		backgroundColor: '#f8f9fa',
		border: '1px solid #e6e6e6',
		borderRadius: '8px',
		padding: '12px',
		marginBottom: '16px',
	},
	sectionTitle: {
		fontSize: '16px',
		fontWeight: '600',
		color: '#333',
		marginBottom: '10px',
	},
	subtleLabel: {
		fontSize: '13px',
		fontWeight: '600',
		color: '#666',
		marginBottom: '8px',
	},
	listBox: {
		border: '1px solid #e1e5ea',
		borderRadius: '6px',
		backgroundColor: '#fff',
		padding: '8px',
		maxHeight: '220px',
		overflowY: 'auto',
	},
	listItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '8px 6px',
		borderBottom: '1px solid #f1f1f1',
		gap: '8px',
	},
	listItemText: {
		fontSize: '14px',
		color: '#333',
	},
	listItemActions: {
		display: 'flex',
		alignItems: 'center',
		gap: '6px',
	},
	actionButton: {
		padding: '4px 8px',
		backgroundColor: '#fff',
		border: '1px solid #dcdcdc',
		borderRadius: '4px',
		fontSize: '12px',
		cursor: 'pointer',
		whiteSpace: 'nowrap',
	},
	actionPrimary: {
		backgroundColor: '#eef5ff',
		borderColor: '#cfe1ff',
		color: '#1f5cc4',
	},
	actionDanger: {
		backgroundColor: '#fff4f4',
		borderColor: '#ffd0d0',
		color: '#c82333',
	},
	iconButton: {
		padding: '4px 8px',
		backgroundColor: '#fff',
		border: '1px solid #dcdcdc',
		borderRadius: '4px',
		fontSize: '12px',
		cursor: 'pointer',
	},
	inlineHelp: {
		fontSize: '13px',
		color: '#6c757d',
		marginTop: '10px',
	},
	emptyState: {
		fontSize: '13px',
		color: '#6c757d',
		padding: '8px 4px',
	},
	stationCard: {
		border: '1px solid #e6e6e6',
		borderRadius: '8px',
		padding: '12px',
		backgroundColor: '#fff',
		marginBottom: '12px',
	},
	stationCardHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '10px',
	},
	stationTitle: {
		fontSize: '15px',
		fontWeight: '600',
		color: '#333',
	},
	stationMeta: {
		fontSize: '12px',
		color: '#6c757d',
		marginTop: '2px',
	},
	countPill: {
		backgroundColor: '#eef5ff',
		color: '#1f5cc4',
		borderRadius: '999px',
		padding: '2px 10px',
		fontSize: '12px',
		fontWeight: '600',
	},
	inlineItemForm: {
		border: '1px solid #e4ecf7',
		backgroundColor: '#f8fbff',
		borderRadius: '6px',
		padding: '10px 10px 2px',
		marginBottom: '10px',
	},
	inlineFieldLabel: {
		display: 'block',
		fontSize: '11px',
		fontWeight: 600,
		color: '#58677a',
		marginBottom: '4px',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
};

const requirementsStyles = {
	summaryCard: {
		backgroundColor: '#f8f9fb',
		border: '1px solid #e6e9ef',
		borderRadius: '8px',
		padding: '12px 16px',
	},
	sectionCard: {
		backgroundColor: '#fbfcfe',
		border: '1px solid #e6e9ef',
		borderRadius: '8px',
		padding: '12px 16px',
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '8px',
	},
	sectionTitle: {
		fontSize: '12px',
		fontWeight: '700',
		color: '#4a5568',
		textTransform: 'uppercase',
		letterSpacing: '0.4px',
	},
	sectionMeta: {
		fontSize: '12px',
		color: '#718096',
	},
	capacityNote: {
		marginTop: '10px',
		paddingTop: '10px',
		borderTop: '1px solid #e6e9ef',
		fontSize: '13px',
		color: '#52606d',
	},
	tableWrapper: {
		backgroundColor: '#ffffff',
		border: '1px solid #eef1f5',
		borderRadius: '6px',
		overflow: 'hidden',
	},
	stationCard: {
		backgroundColor: '#f8fafc',
		border: '1px solid #e6e9ef',
		borderRadius: '8px',
		padding: '12px',
		marginBottom: '12px',
	},
	stationHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '8px',
	},
	pill: {
		backgroundColor: '#eef2ff',
		color: '#3b5ccc',
		borderRadius: '999px',
		padding: '2px 10px',
		fontSize: '12px',
		fontWeight: '600',
	},
	editedBadge: {
		backgroundColor: '#e6f4ea',
		color: '#2f7a3e',
		borderRadius: '999px',
		padding: '2px 10px',
		fontSize: '12px',
		fontWeight: '600',
		border: '1px solid #cce7d3',
	},
	editStationCard: {
		backgroundColor: '#ffffff',
		border: '1px solid #e6e9ef',
		borderRadius: '8px',
		padding: '12px',
		marginBottom: '12px',
	},
	editStationHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '8px',
	},
	editActions: {
		display: 'flex',
		gap: '6px',
		alignItems: 'center',
		flexWrap: 'wrap',
	},
	addRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		flexWrap: 'wrap',
	},
};


export default Framework.Components.withRootState(MrpJob);
